import {
  keepPreviousData,
  mutationOptions,
  type QueryClient,
  type QueryKey,
  queryOptions,
  useIsMutating,
  useMutation,
  UseMutationOptions,
  useQueries,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
  useSuspenseQuery,
} from '@tanstack/react-query'
import _ from 'lodash'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { match } from 'ts-pattern'
import { z, ZodError } from 'zod'
import { buildPath } from '../build-path.ts'
import { invariantType } from '../type-utils.ts'
import { ApiError } from './api-error'
import {
  type AddRepositoryRequest,
  AddRepositoryRequestSchema,
  AllowedMethodsSchema,
  type AnalysisCancellationResponse,
  AnalysisCancellationResponseSchema,
  AnalysisPaginatedResponseSchema,
  type AnalysisResponse,
  AnalysisResponseSchema,
  AnalysisStateResponse,
  type ApiRootResponse,
  ApiRootResponseSchema,
  AzureOrganizationPaginatedResponseSchema,
  AzureProjectPaginatedResponseSchema,
  AzureRepositoryPaginatedResponseSchema,
  BitbucketRepositoryPaginatedResponseSchema,
  BitbucketWorkspacePaginatedResponseSchema,
  type ChangesetPaginatedResponse,
  ChangesetPaginatedResponseSchema,
  type CodingGuidelinesResponse,
  CodingGuidelinesResponseSchema,
  type CreateIntegrationRequest,
  CreateIntegrationRequestSchema,
  type CreatePushRequest,
  type CreatePushResponse,
  CreatePushResponseSchema,
  type CreateRepositoryIntegrationRequest,
  CreateRepositoryIntegrationRequestSchema,
  type CreateWorkflowRequest,
  CreateWorkflowRequestSchema,
  CurrentUserResponseSchema,
  ExploitChainsPhaseSchema,
  type ExploitChainsResponse,
  ExploitChainsResponseSchema,
  type FeedbackRating,
  type FeedbackSubmissionPaginatedResponse,
  FeedbackSubmissionPaginatedResponseSchema,
  type FeedbackSubmissionResponse,
  FeedbackSubmissionResponseSchema,
  type FindingAggregateResponse,
  FindingAggregateResponseSchema,
  type FindingEmbedParameter,
  FindingOverlayPhaseSchema,
  type FindingOverlayResponse,
  FindingOverlayResponseSchema,
  type FindingOverlaysResponse,
  FindingOverlaysResponseSchema,
  type FindingResponseWithoutEmbed,
  FindingResponseWithoutEmbedSchema,
  type FindingResultPaginatedResponse,
  FindingResultPaginatedResponseSchema,
  getIsIntegrationTypeSupportedByApi,
  type GetPushesResponse,
  GetPushesResponseSchema,
  GitLabProjectPaginatedResponseSchema,
  type Integration,
  IntegrationPaginatedResponseSchema,
  type IntegrationType,
  isInFlightGenerationStatus,
  type OrganizationPreferencesResponse,
  OrganizationPreferencesResponseSchema,
  type PaginatedResponse,
  type PatchesCreatePatchRequest,
  PatchesCreatePatchRequestSchema,
  type PatchPaginatedResponse,
  PatchPaginatedResponseSchema,
  type PolarisStatusLookupResponse,
  PolarisStatusLookupResponseSchema,
  RefreshRequestResponseSchema,
  RefreshRequestsResponseSchema,
  type RefreshRequestStateResponse,
  RepositoryIntegrationPaginatedResponseSchema,
  RepositoryPaginatedResponseSchema,
  RepositorySchema,
  type ResultsVerificationResponse,
  ResultsVerificationResponseSchema,
  type SbomResponse,
  SbomResponseSchema,
  type ScalePaginatedResponse,
  ScalePaginatedResponseSchema,
  ScaleSchema,
  type ScanAggregateResponse,
  ScanAggregateResponseSchema,
  type ScanAnalysisState,
  type ScanResponse,
  ScanResponseSchema,
  type ScaStatusResponse,
  type ScorecardsResponse,
  ScorecardsResponseSchema,
  type SupportBundleResponse,
  SupportBundleResponseSchema,
  type SystemStatusesResponse,
  SystemStatusesResponseSchema,
  type ThreatModelContent,
  ThreatModelContentSchema,
  ThreatModelPhaseSchema,
  type ThreatModelResponse,
  ThreatModelResponseSchema,
  type Tool,
  type UpdateCodingGuidelinesRequest,
  type UpdateIntegrationRequest,
  UpdateIntegrationRequestSchema,
  type UpdateWorkflowRequest,
  UpdateWorkflowRequestSchema,
  WorkflowPaginatedResponseSchema,
  type WritableIntegrationType,
} from './api-schemas.ts'

import type { DefaultError } from '@tanstack/react-query'
import { type SSEStream, useSSEStream } from '../../hooks/use-sse-stream.ts'
import { SCAN_FINDINGS_PARAMS } from './query-params'
import type { CreateSbomRequestFormData, CreateScanRequestFormData } from './request-form-data.ts'
import {
  type AnalysisStreamEvent,
  AnalysisStreamEventSchema,
  type ExploitChainsStreamEvent,
  ExploitChainsStreamEventSchema,
  type FindingOverlaysStreamEvent,
  FindingOverlaysStreamEventSchema,
  type SupportBundlePhaseEvent,
  SupportBundlePhaseEventSchema,
  type ThreatModelStreamEvent,
  ThreatModelStreamEventSchema,
} from './sse-schemas.ts'

// #region: utils
export class ZodParseError extends Error {
  public readonly zodError: ZodError
  public readonly path: string
  public readonly schemaName?: string

  constructor(path: string, zodError: ZodError, schemaName?: string) {
    super(`Zod parse error at ${path}: ${zodError.message}`)
    this.name = 'ZodParseError'
    this.path = path
    this.zodError = zodError
    this.schemaName = schemaName
  }

  get issues(): z.core.$ZodIssue[] {
    return this.zodError.issues
  }
}

type LogZodErrorLogger = ({
  path,
  name,
  message,
  stack,
  issues,
}: {
  path: string
  name: string
  message: string
  stack?: string
  issues: z.core.$ZodIssue[]
}) => unknown

export async function logZodError<T>(
  path: string,
  schema: z.ZodType,
  action: () => Promise<T>,
  logger: LogZodErrorLogger = console.error
): Promise<T> {
  try {
    return await action()
  } catch (error: unknown) {
    if (error instanceof ZodError) {
      const { name, message, stack, issues } = error
      logger({
        path,
        name,
        message,
        stack,
        issues,
      })

      throw new ZodParseError(path, error, schema.description)
    }

    throw error
  }
}

type FetchWrapperParams<T extends z.ZodType> = {
  path: string
  options?: RequestInit
  schema: T
  successStatusCodes?: number[]
  deserializeFn?: (response: Response) => Promise<unknown>
}

/**
 * Wrapper function for API GET requests
 *
 * Makes a fetch request to the specified path, validates the response against the provided schema,
 * and handles errors by throwing an ApiError with appropriate status and body.
 *
 * @param {Object} params - Parameters for the fetch request
 * @param {string} params.path - The API endpoint path
 * @param {RequestInit} [params.options] - Optional fetch request options
 * @param {z.ZodType} params.schema - Zod schema for validating the response
 * @param {number[]} [params.successStatusCodes] - HTTP status codes considered successful (defaults to [200, 201, 204])
 * @param {Function} [params.deserializeFn] - Custom function to deserialize the response (defaults to response.json())
 * @returns {Promise<z.infer<T>>} - Parsed and validated response data
 * @throws {ApiError} - If the response status is not in the successStatusCodes list
 */
const fetchWrapperQuery = async <T extends z.ZodType>({
  path,
  options = {},
  schema,
  successStatusCodes = [200, 201, 204],
  deserializeFn = (response: Response) => response.json() as Promise<unknown>,
}: FetchWrapperParams<T>): Promise<z.infer<T>> => {
  const response = await fetch(path, {
    headers: {
      'X-Requested-With': 'JavaScript',
      Accept: 'application/hal+json, application/json',
    },
    ...options,
  })

  if (!successStatusCodes.includes(response.status)) {
    throw new ApiError(response.status, await response.text(), path)
  }

  return await logZodError<z.infer<T>>(path, schema, async () => schema.parse(await deserializeFn(response)))
}

type FetchWrapperMutationParams<T extends z.ZodType = z.ZodType> = {
  path: string
  options?: RequestInit
  schema: T
  successStatusCodes?: number[]
  deserializeFn?: (response: Response) => Promise<unknown>
}

/**
 * Wrapper function for API mutation requests (POST, PUT, DELETE, etc.)
 *
 * Makes a fetch request to the specified path with JSON content type headers,
 * validates the response against the provided schema, and handles errors.
 *
 * @param {Object} params - Parameters for the mutation request
 * @param {string} params.path - The API endpoint path
 * @param {RequestInit} [params.options] - Optional fetch request options
 * @param {z.ZodType} [params.schema] - Zod schema for validating the response
 * @param {number[]} [params.successStatusCodes] - HTTP status codes considered successful (defaults to [200, 201, 204])
 * @param {Function} [params.deserializeFn] - Custom function to deserialize the response (defaults to returning undefined)
 * @returns {Promise<z.infer<T>>} - Parsed and validated response data
 * @throws {ApiError} - If the response status is not in the successStatusCodes list
 */
const fetchWrapperMutation = async <T extends z.ZodType = z.ZodType>({
  path,
  options,
  schema,
  successStatusCodes = [200, 201, 204],
  deserializeFn = () => Promise.resolve(undefined),
}: FetchWrapperMutationParams<T>): Promise<z.infer<T>> => {
  const response = await fetch(path, {
    headers: {
      'X-Requested-With': 'JavaScript',
      Accept: 'application/hal+json, application/json',
      'Content-Type': 'application/json',
    },
    ...options,
  })

  if (!successStatusCodes.includes(response.status)) {
    throw new ApiError(response.status, await response.text(), path)
  }

  return await logZodError<z.infer<T>>(path, schema, async () => schema.parse(await deserializeFn(response)))
}

type OnSuccess<T, TVariables = void, TContext = unknown> = UseMutationOptions<
  T,
  DefaultError,
  TVariables,
  TContext
>['onSuccess']
type OnError<T, TVariables = void, TContext = unknown> = UseMutationOptions<
  T,
  DefaultError,
  TVariables,
  TContext
>['onError']
type OnMutate<TVariables = void, TContext = unknown> = UseMutationOptions<
  unknown,
  DefaultError,
  TVariables,
  TContext
>['onMutate']
type OnSettled<T, TVariables = void, TContext = unknown> = UseMutationOptions<
  T,
  DefaultError,
  TVariables,
  TContext
>['onSettled']

type PageQueryParams = {
  'page-number': string
  'page-size': string
}

const createPageParams = (pageIndex?: number, pageSize?: number): URLSearchParams => {
  if (pageIndex === undefined || pageSize === undefined) {
    return new URLSearchParams()
  }
  return new URLSearchParams({
    'page-number': pageIndex.toString(),
    'page-size': pageSize.toString(),
  } satisfies PageQueryParams)
}

// #endregion utils

// #region: queries

export const useGetSystemStatuses = (
  options: Omit<UseQueryOptions<SystemStatusesResponse>, 'queryKey' | 'queryFn'> = {}
) =>
  useQuery({
    queryKey: ['system-statuses'],
    queryFn: () =>
      fetchWrapperQuery({
        path: '/api/v1/system-statuses',
        schema: SystemStatusesResponseSchema,
      }),
    ...options,
  })

export const useCurrentUser = () =>
  useQuery({
    queryKey: ['current-user'],
    queryFn: () =>
      fetchWrapperQuery({
        path: '/api/v1/users/me',
        schema: CurrentUserResponseSchema,
      }),
  })

/**
 * Whether the signed-in user holds the admin role, or `undefined` when the answer is unknown.
 *
 * `undefined` covers three cases that a caller cannot tell apart and should not try to: the
 * request is in flight, it failed, or it came back without a `roles` array. Callers that hide
 * privileged controls must treat `undefined` as "we don't know" rather than "no" — collapsing it
 * to `false` flashes a permission message at an admin on every page load, and names the wrong
 * cause when the platform simply never sent the field.
 *
 * A failed request will not resolve on its own, so a caller that hides a control on `undefined`
 * should pair this with `useCurrentUser().isError` and explain itself, rather than leave an admin
 * with neither the control nor a reason.
 *
 * The `roles` array from `/api/v1/users/me` is the only authorization input the front end gets.
 * Enforcement is off by default, and with it off every signed-in user has full access — so on a
 * default install this is `true` for everyone. The fixtures and stories that model that default
 * point here rather than restating it. The platform side owns that behavior and documents it
 * under "Enforcing the admin role" in the enterprise-server docs.
 */
export const useIsAdmin = () => {
  const { data } = useCurrentUser()
  return data?.roles ? data.roles.includes('admin') : undefined
}

type GetIntegrationsOptionsInput = {
  page?: PageOptions
}

const getIntegrationsOptions = ({ page = { number: 0, size: 10 } }: GetIntegrationsOptionsInput = {}) =>
  queryOptions({
    queryKey: ['integrations', page],
    queryFn: () => {
      const queryParams = createPageParams(page.number, page.size)

      return fetchWrapperQuery({
        path: `/api/v1/integrations?${queryParams}`,
        schema: IntegrationPaginatedResponseSchema,
      })
    },
  })

export const useGetIntegrations = (input?: GetIntegrationsOptionsInput) => useQuery(getIntegrationsOptions(input))

export const useSuspenseGetIntegrations = (input?: GetIntegrationsOptionsInput) =>
  useSuspenseQuery(getIntegrationsOptions(input))

/**
 * Warms the first page of the window `useAllIntegrations` reads, so the surfaces that list
 * integrations paint from cache. Warming a different window warms nothing anyone asks for.
 */
export const usePrefetchIntegrations = (input: Omit<GetIntegrationsOptionsInput, 'page'> = {}) => {
  const queryClient = useQueryClient()
  useEffect(() => {
    queryClient.prefetchQuery(
      getIntegrationsOptions({ ...input, page: { number: 0, size: ALL_INTEGRATIONS_PAGE_SIZE } })
    )
  }, [queryClient, input])
}

const ALL_INTEGRATIONS_PAGE_SIZE = 100

export const useGetAllIntegrationsPages = (input: Omit<GetIntegrationsOptionsInput, 'page'> = {}) =>
  useGetAllPages({
    getPageQueryOptions: pageOptions => getIntegrationsOptions({ ...input, page: pageOptions }),
    pageSize: ALL_INTEGRATIONS_PAGE_SIZE,
  })

/**
 * Every integration the org has, across pages. The one answer to "which integrations exist": a page
 * window decided per-surface caps the list at whatever that surface guessed, and splits the cache so
 * two surfaces over the same collection open two requests.
 */
export const useAllIntegrations = (): { integrations: Integration[]; isLoading: boolean } => {
  // `useGetAllPages` derives its page list *from* page 0, so its result array is empty both before
  // that request settles and when the collection is genuinely empty — it cannot report the first
  // load. Subscribing to page 0 here reads the same query key, so it costs no extra request.
  const firstPage = useGetIntegrations({ page: { number: 0, size: ALL_INTEGRATIONS_PAGE_SIZE } })
  const results = useGetAllIntegrationsPages()
  return {
    integrations: results.flatMap(result => result.data?._embedded?.items ?? []),
    isLoading: firstPage.isLoading || results.some(result => result.isLoading),
  }
}

const ALLOWED_METHODS_STALE_TIME_MS = 300_000

const getIntegrationsAllowedMethodsOptions = () =>
  queryOptions({
    // Deliberately outside the ['integrations', …] key space: what an installation permits does not
    // change when its instances do, so the write hooks' invalidation must not re-run this probe.
    queryKey: ['integrations-allowed-methods'],
    queryFn: () =>
      fetchWrapperQuery({
        path: '/api/v1/integrations',
        options: { method: 'OPTIONS' },
        schema: AllowedMethodsSchema,
        // 405 is the documented answer for a read-only installation, and it is the response that
        // carries `Allow`. Treating it as a failure would throw before the header could be read.
        successStatusCodes: [200, 204, 405],
        deserializeFn: async response => response.headers.get('Allow'),
      }),
    // An unanswered probe is an expected negative, not a failed data fetch: the caller reads it as
    // "management is on", so there is nothing to gain from retries or a Sentry event.
    retry: false,
    throwOnError: false,
    // Not `Infinity`: the answer is persisted to localStorage, so an installation that turns
    // management on would stay read-only across reloads until the cache happened to be evicted.
    staleTime: ALLOWED_METHODS_STALE_TIME_MS,
  })

export const useGetIntegrationsAllowedMethods = () => useQuery(getIntegrationsAllowedMethodsOptions())

export const useGetRepositoryFromHalLink = ({ path, enabled = true }: { path?: string; enabled?: boolean }) =>
  useQuery({
    queryKey: ['repository', path],
    queryFn: () =>
      fetchWrapperQuery({
        path: path!,
        schema: RepositorySchema,
      }),
    enabled: enabled && !!path,
  })

const getRepositoryQueryKey = (repositoryId?: string) => ['repository', repositoryId]

const getRepositoryOptions = (repositoryId: string | undefined) =>
  queryOptions({
    queryKey: getRepositoryQueryKey(repositoryId),
    queryFn: () =>
      fetchWrapperQuery({
        path: buildPath('/api/v1/repositories/:repositoryId', { repositoryId: repositoryId ?? '' }),
        schema: RepositorySchema,
      }),
    enabled: !!repositoryId,
  })

export const useGetRepository = ({ repositoryId }: { repositoryId?: string }) =>
  useQuery(getRepositoryOptions(repositoryId))

export const useSuspenseGetRepository = ({ repositoryId }: { repositoryId: string }) =>
  useSuspenseQuery(getRepositoryOptions(repositoryId))

const getRepositoriesQueryKey = (params?: Parameters<typeof useGetRepositories>[0]) =>
  params ? ['repositories', params.pageIndex, params.pageSize, params.searchTerm] : ['repositories']

export const useGetRepositories = ({
  pageIndex,
  pageSize,
  searchTerm = '',
}: {
  pageIndex: number
  pageSize: number
  searchTerm?: string
}) => {
  return useQuery({
    placeholderData: keepPreviousData,
    queryKey: getRepositoriesQueryKey({ pageIndex, pageSize, searchTerm }),
    queryFn: async () => {
      const queryParams = createPageParams(pageIndex, pageSize)

      if (searchTerm) {
        queryParams.append('name', searchTerm)
      }

      return fetchWrapperQuery({
        path: `/api/v1/repositories?${queryParams}`,
        schema: RepositoryPaginatedResponseSchema,
      })
    },
  })
}

export const getScanQueryKey = ({ id }: Partial<Pick<ScanResponse, 'id'>>) => ['scan', id]

export const getScanOptions = ({
  id,
  enabled = true,
  ...rest
}: Partial<Omit<UseQueryOptions<ScanResponse, ApiError>, 'queryKey' | 'queryFn'>> &
  Parameters<typeof getScanQueryKey>[0]) =>
  queryOptions({
    queryKey: getScanQueryKey({ id }),
    queryFn: () =>
      fetchWrapperQuery({
        path: buildPath('/api/v1/scans/:id', { id: id ?? '' }),
        schema: ScanResponseSchema,
      }),
    enabled: enabled && !!id,
    ...rest,
  })

export const useGetScan = (options: Parameters<typeof getScanOptions>[0]) => useQuery(getScanOptions(options))

export const getScansQueryKey = ({
  page,
  filters,
}: {
  page: PageOptions
  filters: {
    repositoryIds?: string[]
    hasAnalysis?: boolean
    tools?: string[]
    analysisState?: ScanAnalysisState
  }
}) => (filters ? ['scans', page, filters] : ['scans', page])

export const getScansOptions = ({
  page = { number: 0, size: 10 },
  repositoryIds,
  hasAnalysis,
  tools,
  analysisState,
  enabled = true,
  refetchInterval,
}: {
  page?: PageOptions
  repositoryIds?: string[]
  hasAnalysis?: boolean
  tools?: Tool[]
  analysisState?: ScanAnalysisState
  enabled?: boolean
  refetchInterval?: number | false
}) =>
  queryOptions({
    queryKey: getScansQueryKey({
      page,
      filters: { repositoryIds, hasAnalysis, tools, analysisState },
    }),
    queryFn: () => {
      const queryParams = createPageParams(page.number, page.size)

      if (repositoryIds && repositoryIds.length > 0) {
        repositoryIds.forEach(id => queryParams.append('repository-ids', id))
      }

      if (hasAnalysis !== undefined) {
        queryParams.append('has-analysis', String(hasAnalysis))
      }

      if (tools && tools.length > 0) {
        tools.forEach(tool => queryParams.append('tools', tool))
      }

      if (analysisState) {
        queryParams.append('analysis-state', analysisState)
      }

      return fetchWrapperQuery({
        path: `/api/v1/scans?${queryParams}`,
        schema: ScanAggregateResponseSchema,
      })
    },
    enabled,
    refetchInterval,
  })

export const useGetScans = (options: Parameters<typeof getScansOptions>[0]) => useQuery(getScansOptions(options))

export const useGetScansWithRepositories = (options: Parameters<typeof getScansOptions>[0]) =>
  useQueryWithLinks(getScansOptions(options), [
    {
      key: '_embedded.items._links.repository.href',
      schema: RepositorySchema,
    },
  ])

export const useGetScanAnalysis = ({
  analysisId,
  enabled = true,
  ...rest
}: {
  analysisId?: string
} & Omit<UseQueryOptions<AnalysisResponse>, 'queryKey' | 'queryFn'>) =>
  useQuery({
    queryKey: ['scan-analysis', analysisId],
    queryFn: () =>
      fetchWrapperQuery({
        path: buildPath('/api/v1/analyses/:analysisId', { analysisId: analysisId ?? '' }),
        schema: AnalysisResponseSchema,
      }),
    enabled: enabled && !!analysisId,
    ...rest,
  })

export const useGetAnalysisFromHalLink = ({ path, enabled = true }: { path?: string; enabled?: boolean }) =>
  useQuery({
    queryKey: ['analysis', path],
    queryFn: () => fetchWrapperQuery({ path: path!, schema: AnalysisResponseSchema }),
    enabled: enabled && !!path,
  })

export const useGetTriageResultArticle = ({
  resultId,
  enabled = true,
  ...rest
}: {
  resultId?: string
} & Omit<UseQueryOptions<string>, 'queryKey' | 'queryFn'>) =>
  useQuery({
    queryKey: ['triageResultArticle', resultId],
    queryFn: () =>
      fetchWrapperQuery({
        path: buildPath('/api/v1/triage-results/:resultId/article', { resultId: resultId ?? '' }),
        options: {
          headers: {
            'X-Requested-With': 'JavaScript',
            Accept: 'text/markdown',
          },
        },
        schema: z.string(),
        successStatusCodes: [200, 204],
        deserializeFn: (response: Response) => response.text(),
      }),
    enabled: enabled && !!resultId,
    ...rest,
  })

export const useGetScaResultArticle = ({ resultId, enabled = true }: { resultId?: string; enabled?: boolean }) =>
  useQuery({
    queryKey: ['scaResultArticle', resultId],
    queryFn: () =>
      fetchWrapperQuery({
        path: buildPath('/api/v1/sca-results/:resultId/article', { resultId: resultId ?? '' }),
        options: {
          headers: {
            'X-Requested-With': 'JavaScript',
            Accept: 'text/markdown',
          },
        },
        schema: z.string(),
        successStatusCodes: [200, 204],
        deserializeFn: (response: Response) => response.text(),
      }),
    enabled: enabled && !!resultId,
  })

export const useGetScaResultReport = ({
  resultId,
  enabled = true,
  ...rest
}: {
  resultId?: string
} & Omit<UseQueryOptions<string>, 'queryKey' | 'queryFn'>) =>
  useQuery({
    queryKey: ['scaResultReport', resultId],
    queryFn: () =>
      fetchWrapperQuery({
        path: buildPath('/api/v1/sca-results/:resultId/report', { resultId: resultId ?? '' }),
        options: {
          headers: {
            'X-Requested-With': 'JavaScript',
            Accept: 'text/markdown',
          },
        },
        schema: z.string(),
        successStatusCodes: [200, 204],
        deserializeFn: (response: Response) => response.text(),
      }),
    enabled: enabled && !!resultId,
    retry: 1,
    ...rest,
  })

export const useGetTriageResultReport = ({
  resultId,
  enabled = true,
  ...rest
}: {
  resultId?: string
} & Omit<UseQueryOptions<string>, 'queryKey' | 'queryFn'>) =>
  useQuery({
    queryKey: ['triageResultReport', resultId],
    queryFn: () =>
      fetchWrapperQuery({
        path: buildPath('/api/v1/triage-results/:resultId/report', { resultId: resultId ?? '' }),
        options: {
          headers: {
            'X-Requested-With': 'JavaScript',
            Accept: 'text/markdown',
          },
        },
        schema: z.string(),
        successStatusCodes: [200, 204],
        deserializeFn: (response: Response) => response.text(),
      }),
    enabled: enabled && !!resultId,
    ...rest,
  })

export const useGetTriageResultVerification = ({
  resultId,
  enabled = true,
  ...rest
}: {
  resultId?: string
} & Omit<UseQueryOptions<ResultsVerificationResponse | null>, 'queryKey' | 'queryFn'>) =>
  useQuery({
    queryKey: ['triageResultVerification', resultId],
    queryFn: () =>
      fetchWrapperQuery({
        path: buildPath('/api/v1/triage-results/:resultId/verification', { resultId: resultId ?? '' }),
        schema: z.union([ResultsVerificationResponseSchema, z.null()]),
        successStatusCodes: [200, 404],
        deserializeFn: async (response: Response) => {
          if (response.status === 404) {
            return null
          }

          return (await response.json()) as unknown
        },
      }),
    enabled: enabled && !!resultId,
    ...rest,
  })

/**
 * Helper function to append query parameters to URLSearchParams
 * Handles both single values and arrays of values
 */
const appendQueryParam = (params: URLSearchParams, key: string, value: string | string[]) => {
  if (Array.isArray(value)) {
    for (const item of value) {
      params.append(key, item)
    }
  } else {
    params.append(key, value)
  }
}

/**
 * The findings filters, by the names the client uses for them.
 *
 * One list, because the same projection of the filter state feeds two consumers: the findings query
 * and the CSV export. When each spelled its own list, a filter added or renamed in one and missed
 * in the other type-checked and silently stopped filtering that consumer's results.
 */
export type ScanFindingsFilters = {
  analyzed?: boolean
  fixStatuses?: string[]
  triageStatuses?: string[]
  triageSuggestedStatuses?: string[]
  patchStatuses?: string[]
  scaStatus?: ScaStatusResponse
  fixConfidences?: string[]
  scaClassifications?: string[]
  severityUpdates?: string[]
  includeSeverityLabels?: string[]
  includeSuggestedSeverityLabels?: string[]
  minimumSeverityScore?: number
  maximumSeverityScore?: number
  minimumSuggestedSeverityScore?: number
  maximumSuggestedSeverityScore?: number
  sort?: string
  order?: string
}

/** The filters as the request layer takes them: paging spelled out, free text as the API names it. */
export type ScanFindingsQueryParams = ScanFindingsFilters & {
  pageIndex?: number
  pageSize?: number
  q?: string
}

export const buildScanFindingsQueryParams = ({
  pageIndex,
  pageSize,
  analyzed,
  fixStatuses,
  triageStatuses,
  triageSuggestedStatuses,
  patchStatuses,
  scaStatus,
  fixConfidences,
  scaClassifications,
  severityUpdates,
  includeSeverityLabels,
  includeSuggestedSeverityLabels,
  minimumSeverityScore,
  maximumSeverityScore,
  minimumSuggestedSeverityScore,
  maximumSuggestedSeverityScore,
  q,
  sort,
  order,
  embed,
}: ScanFindingsQueryParams & { embed?: FindingEmbedParameter[] }): URLSearchParams => {
  const params = createPageParams(pageIndex, pageSize)

  if (analyzed !== undefined) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.analyzed, String(analyzed))
  }

  if (fixStatuses?.length) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.fixStatus, fixStatuses)
  }

  if (triageStatuses?.length) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.triageStatus, triageStatuses)
  }

  if (triageSuggestedStatuses?.length) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.triageSuggestedStatus, triageSuggestedStatuses)
  }

  if (patchStatuses?.length) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.patchStatus, patchStatuses)
  }

  if (scaStatus) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.scaStatus, scaStatus)
  }

  if (fixConfidences?.length) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.fixConfidences, fixConfidences)
  }

  if (scaClassifications?.length) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.scaClassifications, scaClassifications)
  }

  if (severityUpdates?.length) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.severityUpdates, severityUpdates)
  }

  if (includeSeverityLabels?.length) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.includeSeverityLabel, includeSeverityLabels)
  }

  if (includeSuggestedSeverityLabels?.length) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.includeSuggestedSeverityLabel, includeSuggestedSeverityLabels)
  }

  if (minimumSeverityScore !== undefined) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.minimumSeverityScore, `${minimumSeverityScore}`)
  }

  if (maximumSeverityScore !== undefined) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.maximumSeverityScore, `${maximumSeverityScore}`)
  }

  if (minimumSuggestedSeverityScore !== undefined) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.minimumSuggestedSeverityScore, `${minimumSuggestedSeverityScore}`)
  }

  if (maximumSuggestedSeverityScore !== undefined) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.maximumSuggestedSeverityScore, `${maximumSuggestedSeverityScore}`)
  }

  if (q) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.q, q)
  }

  if (sort) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.sort, sort.toLowerCase())
  }

  if (order) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.order, order.toLowerCase())
  }

  if (embed?.length) {
    appendQueryParam(params, SCAN_FINDINGS_PARAMS.embed, embed)
  }

  return params
}

export const useGetFixResultChangesets = ({
  resultId,
  pageIndex = 0,
  pageSize = 10,
  enabled = true,
  ...rest
}: {
  resultId?: string
  pageIndex?: number
  pageSize?: number
} & Omit<UseQueryOptions<ChangesetPaginatedResponse>, 'queryKey' | 'queryFn'>) =>
  useQuery({
    queryKey: ['changesetsForFixResult', resultId, pageIndex, pageSize],
    queryFn: () =>
      fetchWrapperQuery({
        path: `${buildPath('/api/v1/fix-results/:resultId/changesets', { resultId: resultId ?? '' })}?page-number=${pageIndex}&page-size=${pageSize}`,
        schema: ChangesetPaginatedResponseSchema,
      }),
    enabled: enabled && !!resultId,
    ...rest,
  })

export const useGetFixResultPatches = ({
  resultId,
  pageIndex = 0,
  pageSize = 10,
  enabled = true,
  ...rest
}: {
  resultId?: string
  pageIndex?: number
  pageSize?: number
} & Omit<UseQueryOptions<PatchPaginatedResponse>, 'queryKey' | 'queryFn'>) =>
  useQuery({
    queryKey: ['patchesForFixResult', resultId, pageIndex, pageSize],
    queryFn: () =>
      fetchWrapperQuery({
        path: `${buildPath('/api/v1/fix-results/:resultId/patches', { resultId: resultId ?? '' })}?page-number=${pageIndex}&page-size=${pageSize}`,
        schema: PatchPaginatedResponseSchema,
      }),
    enabled: enabled && !!resultId,
    ...rest,
  })

type GetAzureOrganizationsOptionsInput = {
  page?: PageOptions
  enabled?: boolean
}

const getAzureOrganizationsOptions = ({
  page = { number: 0, size: 10 },
  enabled = true,
}: GetAzureOrganizationsOptionsInput = {}) =>
  queryOptions({
    queryKey: ['azure-organizations', page],
    queryFn: () =>
      fetchWrapperQuery({
        path: `/api/v1/integrations/azure-default/organizations?${createPageParams(page.number, page.size)}`,
        schema: AzureOrganizationPaginatedResponseSchema,
      }),
    enabled,
  })

export const useGetAzureOrganizations = (input?: GetAzureOrganizationsOptionsInput) =>
  useQuery(getAzureOrganizationsOptions(input))

export const useGetAllAzureOrganizationPages = (input: Omit<GetAzureOrganizationsOptionsInput, 'page'> = {}) =>
  useGetAllPages({
    getPageQueryOptions: pageOptions => getAzureOrganizationsOptions({ ...input, page: pageOptions }),
    pageSize: 100,
  })

type GetAzureProjectsOptionsInput = {
  organizationId?: string
  page?: PageOptions
  enabled?: boolean
}

const getAzureProjectsOptions = ({
  organizationId,
  page = { number: 0, size: 200 },
  enabled = true,
}: GetAzureProjectsOptionsInput) =>
  queryOptions({
    queryKey: ['azure-projects', organizationId, page],
    queryFn: () =>
      fetchWrapperQuery({
        path: `${buildPath('/api/v1/integrations/azure-default/:organizationId/projects', { organizationId: organizationId ?? '' })}?${createPageParams(page.number, page.size)}`,
        schema: AzureProjectPaginatedResponseSchema,
      }),
    enabled: enabled && !!organizationId,
  })

export const useGetAzureProjects = (input: GetAzureProjectsOptionsInput) => useQuery(getAzureProjectsOptions(input))

export const useGetAllAzureProjectPages = (input: Omit<GetAzureProjectsOptionsInput, 'page'>) =>
  useGetAllPages({
    getPageQueryOptions: pageOptions => getAzureProjectsOptions({ ...input, page: pageOptions }),
    pageSize: 200,
  })

type GetAzureRepositoriesOptionsInput = {
  organizationName: string
  projectId: string
  page?: PageOptions
  enabled?: boolean
}

const getAzureRepositoriesOptions = ({
  organizationName,
  projectId,
  page = { number: 0, size: 200 },
  enabled = true,
}: GetAzureRepositoriesOptionsInput) =>
  queryOptions({
    queryKey: ['azure-repositories', organizationName, projectId, page],
    queryFn: () =>
      fetchWrapperQuery({
        path: `${buildPath('/api/v1/integrations/azure-default/:organizationName/projects/:projectId/repositories', { organizationName, projectId })}?${createPageParams(page.number, page.size)}`,
        schema: AzureRepositoryPaginatedResponseSchema,
      }),
    enabled: enabled && !!organizationName && !!projectId,
  })

export const useGetAzureRepositories = (input: GetAzureRepositoriesOptionsInput) =>
  useQuery(getAzureRepositoriesOptions(input))

export const useGetAllAzureRepositoryPages = (input: Omit<GetAzureRepositoriesOptionsInput, 'page'>) =>
  useGetAllPages({
    getPageQueryOptions: pageOptions => getAzureRepositoriesOptions({ ...input, page: pageOptions }),
    pageSize: 200,
  })

export const useGetBitbucketWorkspaces = ({
  pageIndex = 0,
  pageSize = 100,
  enabled = true,
}: {
  pageIndex?: number
  pageSize?: number
  enabled?: boolean
}) =>
  useQuery({
    queryKey: ['bitbucket-workspaces', pageIndex, pageSize],
    queryFn: () =>
      fetchWrapperQuery({
        path: `/api/v1/integrations/bitbucket-default/workspaces?page-number=${pageIndex}&page-size=${pageSize}`,
        schema: BitbucketWorkspacePaginatedResponseSchema,
      }),
    enabled,
  })

export const useGetBitbucketRepositories = ({
  workspaceSlug,
  pageIndex = 0,
  pageSize = 100,
  enabled = true,
}: {
  workspaceSlug: string
  pageIndex?: number
  pageSize?: number
  enabled?: boolean
}) =>
  useQuery({
    queryKey: ['bitbucket-repositories', workspaceSlug, pageIndex, pageSize],
    queryFn: () =>
      fetchWrapperQuery({
        path: `${buildPath('/api/v1/integrations/bitbucket-default/workspaces/:workspaceSlug/repositories', { workspaceSlug })}?page-number=${pageIndex}&page-size=${pageSize}`,
        schema: BitbucketRepositoryPaginatedResponseSchema,
      }),
    enabled,
  })

export const getAnalysesQueryKey = (options?: GetAnalysesOptions) => {
  if (!options) return ['analyses']
  const {
    page,
    filters: { repositoryIds, states, tools, scanIds, updatedOnOrBefore, updatedOnOrAfter },
  } = options
  return [
    'analyses',
    page,
    {
      repositoryIds,
      states,
      tools,
      scanIds,
      updatedOnOrBefore,
      updatedOnOrAfter,
    },
  ]
}

type GetAnalysesOptions = {
  page: PageOptions
  filters: {
    repositoryIds?: string[]
    states?: AnalysisStateResponse[]
    tools?: Tool[]
    scanIds?: string[]
    updatedOnOrBefore?: string
    updatedOnOrAfter?: string
  }
  enabled?: boolean
}

const getAnalysesOptions = ({
  page,
  filters: { repositoryIds, states, tools, scanIds, updatedOnOrBefore, updatedOnOrAfter },
  enabled,
}: GetAnalysesOptions) =>
  queryOptions({
    queryKey: getAnalysesQueryKey({
      page,
      filters: { repositoryIds, states, tools, scanIds, updatedOnOrBefore, updatedOnOrAfter },
    }),
    enabled,
    queryFn: () => {
      const queryParams = createPageParams(page.number, page.size)

      if (updatedOnOrBefore) {
        queryParams.append('updated-on-or-before', updatedOnOrBefore)
      }

      if (updatedOnOrAfter) {
        queryParams.append('updated-on-or-after', updatedOnOrAfter)
      }

      for (const state of states ?? []) {
        queryParams.append('states', state)
      }

      for (const repositoryId of repositoryIds ?? []) {
        queryParams.append('repository-ids', repositoryId)
      }

      for (const tool of tools ?? []) {
        queryParams.append('tools', tool)
      }

      if (scanIds) {
        appendQueryParam(queryParams, 'scan-ids', scanIds)
      }

      return fetchWrapperQuery({
        path: `/api/v1/analyses?${queryParams}`,
        schema: AnalysisPaginatedResponseSchema,
      })
    },
  })

export const useGetAnalyses = (options: GetAnalysesOptions) => useQuery(getAnalysesOptions(options))

export const useGetAllAnalysesPages = (options: Omit<GetAnalysesOptions, 'page'>) =>
  useGetAllPages({
    getPageQueryOptions: pageOptions => getAnalysesOptions({ ...options, page: pageOptions }),
    // assuming 1000 analyses per period as upper bound, 100 per page is fine (10 parallel queries)
    pageSize: 100,
  })

export const useGetAnalysesWithRepositories = (options: Parameters<typeof getAnalysesOptions>[0]) =>
  useQueryWithLinks(getAnalysesOptions(options), [
    {
      key: '_embedded.items._embedded.scan._links.repository.href',
      schema: RepositorySchema,
    },
  ])

export const useGetLatestAnalysisForRepository = ({
  repositoryId,
  states,
  tools,
}: {
  repositoryId: string
  states?: AnalysisStateResponse[]
  tools?: Tool[]
}) => {
  const { data, isLoading } = useGetAnalyses({
    page: { number: 0, size: 1 },
    filters: { repositoryIds: [repositoryId], states, tools },
  })
  return useMemo(() => ({ data: data?._embedded?.items?.[0], isLoading: isLoading }), [data, isLoading])
}

const getGetWorkflowsQueryKey = (params?: Parameters<typeof useGetWorkflows>[0]) =>
  ['workflows', params?.repositoryId, params?.pageIndex, params?.pageSize] as const

export const useGetWorkflows = ({
  repositoryId,
  pageIndex = 0,
  pageSize = 100,
}: {
  repositoryId: string
  pageIndex?: number
  pageSize?: number
}) => {
  return useQuery({
    queryKey: getGetWorkflowsQueryKey({ repositoryId, pageIndex, pageSize }),
    queryFn: () => {
      const queryParams = createPageParams(pageIndex, pageSize)
      return fetchWrapperQuery({
        path: `${buildPath('/api/v1/repositories/:repositoryId/workflows/', { repositoryId })}?${queryParams}`,
        schema: WorkflowPaginatedResponseSchema,
      })
    },
  })
}

export const useGetScaleFromHalLink = ({ path, enabled = true }: { path?: string; enabled?: boolean }) =>
  useQuery({
    queryKey: ['scale', path],
    queryFn: () =>
      fetchWrapperQuery({
        path: path!,
        schema: ScaleSchema,
      }),
    enabled: enabled && !!path,
  })

type GetScalesOptionsInput = {
  page?: PageOptions
} & Omit<UseQueryOptions<ScalePaginatedResponse, ApiError>, 'queryKey' | 'queryFn'>

const getScalesOptions = ({ page = { number: 0, size: 100 }, enabled = true, ...rest }: GetScalesOptionsInput = {}) =>
  queryOptions({
    queryKey: ['scales', page],
    queryFn: () =>
      fetchWrapperQuery({
        path: `/api/v1/scales?${createPageParams(page.number, page.size)}`,
        schema: ScalePaginatedResponseSchema,
      }),
    enabled,
    ...rest,
  })

export const useGetScales = (input?: GetScalesOptionsInput) => useQuery(getScalesOptions(input))

export const useGetAllScalesPages = (input: Omit<GetScalesOptionsInput, 'page'> = {}) =>
  useGetAllPages({
    getPageQueryOptions: pageOptions => getScalesOptions({ ...input, page: pageOptions }),
    pageSize: 100,
  })

const SCAN_FINDINGS_QUERY_KEY_ROOT = 'scan-findings'
const SCAN_FINDING_QUERY_KEY_ROOT = 'scan-finding'
const SCAN_FINDING_RESULTS_QUERY_KEY_ROOT = 'scan-finding-results'

/**
 * The prefix every scan-findings query key starts with, whatever its pagination and filters.
 *
 * Invalidate with this rather than with a key built for particular filters: React Query matches a
 * key partially, so the prefix reaches every page and every filter combination of a scan's
 * findings, and nothing else. `scanId` is required because a prefix ending in `undefined` matches
 * only queries that themselves have no scan — silently nothing, which is what this exists to avoid.
 */
export const getScanFindingsQueryKeyPrefix = (scanId: string) => [SCAN_FINDINGS_QUERY_KEY_ROOT, scanId] as const

/** The prefix every scan-finding query key starts with, whatever it embeds. @see getScanFindingsQueryKeyPrefix */
export const getScanFindingQueryKeyPrefix = (scanId: string) => [SCAN_FINDING_QUERY_KEY_ROOT, scanId] as const

/** The prefix every finding-results query key starts with. @see getScanFindingsQueryKeyPrefix */
export const getScanFindingResultsQueryKeyPrefix = (scanId: string) =>
  [SCAN_FINDING_RESULTS_QUERY_KEY_ROOT, scanId] as const

export type GetScanFindingsQueryOptionsInput = ScanFindingsFilters & {
  scanId?: string
  pagination?: { pageIndex: number; pageSize: number }
  enabled?: boolean
  search?: string
  embed?: FindingEmbedParameter[]
} & Omit<UseQueryOptions<FindingAggregateResponse>, 'queryKey' | 'queryFn'>

const getScanFindingsQueryOptions = ({
  scanId,
  pagination,
  enabled = true,
  analyzed,
  fixStatuses,
  triageStatuses,
  triageSuggestedStatuses,
  patchStatuses,
  includeSeverityLabels,
  includeSuggestedSeverityLabels,
  minimumSeverityScore,
  maximumSeverityScore,
  minimumSuggestedSeverityScore,
  maximumSuggestedSeverityScore,
  search,
  sort,
  order,
  scaStatus,
  fixConfidences,
  scaClassifications,
  severityUpdates,
  embed,
  ...rest
}: GetScanFindingsQueryOptionsInput = {}) => {
  // One key shape: every scan-findings query carries pagination and every filter, so
  // `getScanFindingsQueryKeyPrefix` is the only thing callers should invalidate with. Spelled out
  // rather than spread from that helper so `@tanstack/query/exhaustive-deps` can see `scanId` and
  // the filters here.
  const queryKey = [
    SCAN_FINDINGS_QUERY_KEY_ROOT,
    scanId,
    pagination,
    analyzed,
    fixStatuses,
    triageStatuses,
    triageSuggestedStatuses,
    patchStatuses,
    includeSeverityLabels,
    includeSuggestedSeverityLabels,
    minimumSeverityScore,
    maximumSeverityScore,
    minimumSuggestedSeverityScore,
    maximumSuggestedSeverityScore,
    search,
    sort,
    order,
    scaStatus,
    fixConfidences,
    scaClassifications,
    severityUpdates,
    embed,
  ] as const

  return queryOptions({
    queryKey,
    queryFn: () => {
      const effectivePagination = pagination ?? { pageIndex: 0, pageSize: 10 }
      const params = buildScanFindingsQueryParams({
        pageIndex: effectivePagination.pageIndex,
        pageSize: effectivePagination.pageSize,
        analyzed,
        fixStatuses,
        triageStatuses,
        triageSuggestedStatuses,
        patchStatuses,
        includeSeverityLabels,
        includeSuggestedSeverityLabels,
        minimumSeverityScore,
        maximumSeverityScore,
        minimumSuggestedSeverityScore,
        maximumSuggestedSeverityScore,
        q: search,
        sort,
        order,
        scaStatus,
        fixConfidences,
        scaClassifications,
        severityUpdates,
        embed,
      })

      return fetchWrapperQuery({
        path: `${buildPath('/api/v1/scans/:scanId/findings', { scanId: scanId ?? '' })}?${params.toString()}`,
        schema: FindingAggregateResponseSchema,
      })
    },
    enabled: enabled && !!scanId,
    ...rest,
  })
}

export const useGetScanFindings = (options: GetScanFindingsQueryOptionsInput) =>
  useQuery(getScanFindingsQueryOptions(options))

type GetScanFindingOptionsInput = {
  scanId?: string
  findingId?: string
  embed?: 'results' | 'representative-results'
} & Omit<UseQueryOptions<FindingResponseWithoutEmbed>, 'queryKey' | 'queryFn'>

const getScanFindingOptions = ({ scanId, findingId, embed, enabled = true, ...rest }: GetScanFindingOptionsInput) =>
  queryOptions({
    queryKey: [SCAN_FINDING_QUERY_KEY_ROOT, scanId, findingId, embed] as const,
    queryFn: () => {
      const queryParams = new URLSearchParams()
      if (embed) queryParams.append('embed', embed)
      const queryString = queryParams.toString()
      const path = `${buildPath('/api/v1/scans/:scanId/findings/:findingId', { scanId: scanId ?? '', findingId: findingId ?? '' })}${queryString ? `?${queryString}` : ''}`
      return fetchWrapperQuery({ path, schema: FindingResponseWithoutEmbedSchema })
    },
    enabled: enabled && !!scanId && !!findingId,
    ...rest,
  })

export const useGetScanFinding = (options: GetScanFindingOptionsInput) => useQuery(getScanFindingOptions(options))

export const useGetEachScanFinding = ({
  findingIds,
  ...rest
}: Omit<GetScanFindingOptionsInput, 'findingId'> & {
  findingIds: NonNullable<GetScanFindingOptionsInput['findingId']>[]
}) =>
  useQueries({
    queries: findingIds.map(findingId => getScanFindingOptions({ findingId, ...rest })),
  })

type GetScanFindingResultsQueryOptionsInput = {
  scanId?: string
  findingId?: string
  representative?: boolean
} & Omit<UseQueryOptions<FindingResultPaginatedResponse>, 'queryKey' | 'queryFn'>

const getScanFindingResultsQueryOptions = ({
  scanId,
  findingId,
  representative,
  enabled = true,
  ...rest
}: GetScanFindingResultsQueryOptionsInput) =>
  queryOptions({
    queryKey: [SCAN_FINDING_RESULTS_QUERY_KEY_ROOT, scanId, findingId, representative] as const,
    queryFn: () => {
      const params = new URLSearchParams()

      if (representative !== undefined) {
        appendQueryParam(params, 'representative', String(representative))
      }

      return fetchWrapperQuery({
        path: `${buildPath('/api/v1/scans/:scanId/findings/:findingId/results', { scanId: scanId ?? '', findingId: findingId ?? '' })}${params.toString() ? `?${params.toString()}` : ''}`,
        schema: FindingResultPaginatedResponseSchema,
      })
    },
    enabled: enabled && !!scanId && !!findingId,
    ...rest,
  })

export const useGetScanFindingsResults = ({
  scanId,
  findingIds,
  ...rest
}: Omit<GetScanFindingResultsQueryOptionsInput, 'findingId'> & {
  findingIds: NonNullable<GetScanFindingResultsQueryOptionsInput['findingId']>[]
}) => {
  return useQueries({
    queries: findingIds.map(findingId => getScanFindingResultsQueryOptions({ scanId, findingId, ...rest })),
  })
}

export const useGetScanFindingResults = ({ scanId, findingId, ...rest }: GetScanFindingResultsQueryOptionsInput) => {
  const queryClient = useQueryClient()
  const query = useQuery(getScanFindingResultsQueryOptions({ scanId, findingId, ...rest }))

  const prevOutcomesRef = useRef<Map<string, boolean>>(new Map())
  useEffect(() => {
    const currentItems = query.data?._embedded?.items
    if (!currentItems || !scanId) {
      return
    }

    const currentOutcomes = new Map(currentItems.map(item => [item.id, Boolean(item.outcome)]))

    const hasNewCompletion = [...currentOutcomes].some(
      ([id, hasOutcome]) => hasOutcome && prevOutcomesRef.current.get(id) === false
    )

    if (hasNewCompletion) {
      queryClient.invalidateQueries({ queryKey: getScanFindingsQueryKeyPrefix(scanId) })
    }

    prevOutcomesRef.current = currentOutcomes
  }, [query.data, queryClient, scanId])

  return query
}

export const useGetGitLabProjects = ({
  pageIndex = 0,
  pageSize = 10,
  search,
  enabled = true,
}: {
  pageIndex?: number
  pageSize?: number
  search?: string
  enabled?: boolean
} = {}) => {
  return useQuery({
    queryKey: ['gitlab-projects', pageIndex, pageSize, search],
    queryFn: () => {
      const queryParams = createPageParams(pageIndex, pageSize)

      if (search) {
        queryParams.append('search', search)
      }

      return fetchWrapperQuery({
        path: `/api/v1/integrations/gitlab-default/projects?${queryParams}`,
        schema: GitLabProjectPaginatedResponseSchema,
      })
    },
    enabled,
    placeholderData: keepPreviousData,
  })
}

type RepositoryIntegrationsOptionsInput = {
  repositoryId: string
  page?: PageOptions
}

const getRepositoryIntegrationsOptions = ({
  repositoryId,
  page = { number: 0, size: 10 },
}: RepositoryIntegrationsOptionsInput) =>
  queryOptions({
    queryKey: getRepositoryIntegrationsQueryKey({ repositoryId, page }),
    queryFn: () => {
      const queryParams = createPageParams(page.number, page.size)

      return fetchWrapperQuery({
        path: `${buildPath('/api/v1/repositories/:repositoryId/integrations', { repositoryId })}?${queryParams}`,
        schema: RepositoryIntegrationPaginatedResponseSchema,
      })
    },
  })

const getRepositoryIntegrationsQueryKey = ({ repositoryId, page }: RepositoryIntegrationsOptionsInput) =>
  page ? ['repository-integrations', repositoryId, page] : ['repository-integrations', repositoryId]

export const useGetRepositoryIntegrations = (input: RepositoryIntegrationsOptionsInput) =>
  useQuery(getRepositoryIntegrationsOptions(input))

export const useSuspenseGetRepositoryIntegrations = (input: RepositoryIntegrationsOptionsInput) =>
  useSuspenseQuery(getRepositoryIntegrationsOptions(input))

export const useGetAllRepositoryIntegrationsPages = (input: Omit<RepositoryIntegrationsOptionsInput, 'page'>) =>
  useGetAllPages({
    getPageQueryOptions: pageOptions => getRepositoryIntegrationsOptions({ ...input, page: pageOptions }),
    pageSize: 100,
  })

const REFRESH_REQUEST_LIST_LIMIT = 50
const REFRESH_REQUESTS_POLL_INTERVAL_MS = 15_000
const REFRESH_REQUEST_TERMINAL_STATES = new Set<RefreshRequestStateResponse>([
  'no-revision',
  'completed',
  'failed',
  'superseded',
])
const isNonTerminalRefreshRequestState = (state: RefreshRequestStateResponse) =>
  !REFRESH_REQUEST_TERMINAL_STATES.has(state)

const getRepositoryThreatModelRefreshRequestsQueryKey = (repositoryId: string) =>
  ['repository-threat-model-refresh-requests', repositoryId] as const

export const useGetRepositoryThreatModelRefreshRequests = ({ repositoryId }: { repositoryId: string }) =>
  useQuery({
    queryKey: getRepositoryThreatModelRefreshRequestsQueryKey(repositoryId),
    queryFn: () =>
      fetchWrapperQuery({
        path: `${buildPath('/api/v1/repositories/:repositoryId/threat-model/refresh-requests', {
          repositoryId,
        })}?limit=${REFRESH_REQUEST_LIST_LIMIT}`,
        schema: z.union([RefreshRequestsResponseSchema, z.null()]),
        successStatusCodes: [200, 404],
        // A 404 means the feature flag is off; surface as `null` so the consumer's `data?.items ?? []`
        // renders the empty state and no fabricated envelope is needed.
        deserializeFn: async response => {
          if (response.status === 404) {
            return null
          }
          return (await response.json()) as unknown
        },
      }),
    enabled: !!repositoryId,
    refetchInterval: query =>
      query.state.data?.items.some(item => isNonTerminalRefreshRequestState(item.state))
        ? REFRESH_REQUESTS_POLL_INTERVAL_MS
        : false,
  })

export const invalidateGetRepositoryThreatModelRefreshRequests = (
  client: QueryClient,
  { repositoryId }: { repositoryId: string }
) => client.invalidateQueries({ queryKey: getRepositoryThreatModelRefreshRequestsQueryKey(repositoryId) })

export const useGetRepositoryThreatModelRefreshRequest = ({
  repositoryId,
  requestId,
}: {
  repositoryId: string
  requestId: string
}) =>
  useQuery({
    queryKey: ['repository-threat-model-refresh-request', repositoryId, requestId] as const,
    queryFn: () =>
      fetchWrapperQuery({
        path: buildPath('/api/v1/repositories/:repositoryId/threat-model/refresh-requests/:requestId', {
          repositoryId,
          requestId,
        }),
        schema: z.union([RefreshRequestResponseSchema, z.null()]),
        successStatusCodes: [200, 404],
        deserializeFn: async response => {
          if (response.status === 404) {
            return null
          }
          return (await response.json()) as unknown
        },
      }),
    // The single-item GET performs a live dereference against analysis-service for REGENERATING
    // rows; keep polling while the row is non-terminal so the drawer sees fresh document status.
    refetchInterval: query => {
      const state = query.state.data?.state
      return state && isNonTerminalRefreshRequestState(state) ? REFRESH_REQUESTS_POLL_INTERVAL_MS : false
    },
  })

// #endregion queries

// #region: mutations

export type ExportScanFindingsCsvParams = ScanFindingsQueryParams & { scanId: string; filename?: string }

type FetchScanFindingsCsvParams = Omit<ExportScanFindingsCsvParams, 'filename'>

/**
 * Fetches scan findings as CSV content from the API.
 */
async function fetchScanFindingsCsv({ scanId, ...filterParams }: FetchScanFindingsCsvParams): Promise<string> {
  const params = buildScanFindingsQueryParams(filterParams)
  const queryString = params.toString()
  const url = `${buildPath('/api/v1/scans/:scanId/findings', { scanId })}${queryString ? `?${queryString}` : ''}`

  let response: Response
  try {
    response = await fetch(url, {
      headers: {
        Accept: 'text/csv',
      },
    })
  } catch (error) {
    throw new Error(`Network error while exporting CSV: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }

  if (!response.ok) {
    throw new Error(`Failed to export CSV: ${response.status} ${response.statusText}`)
  }

  return response.text()
}

/**
 * Triggers a browser download for CSV content.
 *
 * Why Blob + DOM manipulation?
 * - Native anchor downloads (`<a href="..." download>`) don't support custom headers
 * - Blob API lets us create a downloadable file from string content
 * - Programmatic anchor click triggers the browser's native download behavior
 */
function triggerCsvDownload(content: string, filename: string): void {
  let downloadUrl: string | undefined
  let anchor: HTMLAnchorElement | undefined

  try {
    const blob = new Blob([content], { type: 'text/csv' })
    downloadUrl = URL.createObjectURL(blob)

    anchor = document.createElement('a')
    anchor.href = downloadUrl
    anchor.download = filename

    document.body.appendChild(anchor)
    anchor.click()
  } finally {
    try {
      if (anchor && document.body.contains(anchor)) {
        document.body.removeChild(anchor)
      }
    } catch {
      // Ignore errors when removing anchor
    }
    try {
      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl)
      }
    } catch {
      // Ignore errors when revoking URL
    }
  }
}

/**
 * Mutation hook for exporting scan findings as CSV.
 * Composes fetchScanFindingsCsv (data fetching) and triggerCsvDownload (browser side effect).
 */
export const useExportScanFindingsCsv = <TContext = unknown>({
  onMutate,
  onSuccess,
  onError,
  onSettled,
}: {
  onMutate?: OnMutate<ExportScanFindingsCsvParams, TContext>
  onSuccess?: OnSuccess<void, ExportScanFindingsCsvParams, TContext>
  onError?: OnError<Error, ExportScanFindingsCsvParams, TContext>
  onSettled?: OnSettled<void, ExportScanFindingsCsvParams, TContext>
} = {}) => {
  return useMutation({
    mutationFn: async ({ scanId, filename = 'findings.csv', ...filterParams }: ExportScanFindingsCsvParams) => {
      const csvContent = await fetchScanFindingsCsv({ scanId, ...filterParams })
      triggerCsvDownload(csvContent, filename)
    },
    onMutate,
    onSuccess,
    onError,
    onSettled,
  })
}

export const createScanFormData = (body: CreateScanRequestFormData) => {
  if (!body || !body.metadata) {
    throw new Error('Metadata is required')
  }

  const formData = new FormData()

  formData.append(
    'metadata',
    new Blob(
      [
        JSON.stringify({
          ...body.metadata,
          sha: body.metadata.sha === '' ? undefined : body.metadata.sha,
          branch: body.metadata.branch === '' ? undefined : body.metadata.branch,
        }),
      ],
      {
        type: 'application/json',
      }
    )
  )

  if (body.files) {
    Object.entries(body.files).forEach(([_, file]) => {
      formData.append('files', file)
    })
  }

  return formData
}

export const usePostUploadScan = ({
  onSuccess,
  onError,
  retry,
}: {
  onSuccess?: OnSuccess<ScanResponse, { repositoryId: string; body: CreateScanRequestFormData }>
  onError?: OnError<unknown, { repositoryId: string; body: CreateScanRequestFormData }>
  retry?: number
} = {}) => {
  const _onSuccess = useCallback<Exclude<typeof onSuccess, undefined>>(
    (data, variables, onMutateResult, context) => {
      const scanId = data.id

      context.client.invalidateQueries({ queryKey: getScanQueryKey({ id: scanId }) })
      context.client.invalidateQueries({ queryKey: getScanFindingsQueryKeyPrefix(scanId) })

      onSuccess?.(data, variables, onMutateResult, context)
    },
    [onSuccess]
  )

  return useMutation({
    mutationFn: async ({ repositoryId, body }) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/repositories/:repositoryId/scans', { repositoryId }),
        options: {
          method: 'POST',
          body: createScanFormData(body),
          // Override the wrapper default content type by setting the header
          // Allow the browser to handle the content type
          headers: {
            'X-Requested-With': 'JavaScript',
          },
        },
        schema: ScanResponseSchema,
        successStatusCodes: [201],
        deserializeFn: (response: Response) => response.json(),
      }),
    onSuccess: _onSuccess,
    onError,
    retry,
  })
}

const createSbomFormData = (body: CreateSbomRequestFormData) => {
  const formData = new FormData()
  formData.append(
    'metadata',
    new Blob([JSON.stringify(body.metadata)], {
      type: 'application/json',
    })
  )
  if (body.file) {
    formData.append('file', body.file)
  }
  return formData
}

export const usePostUploadSbom = (
  options: Omit<
    UseMutationOptions<SbomResponse, DefaultError, { repositoryId: string; body: CreateSbomRequestFormData }>,
    'mutationFn' | 'mutationKey'
  > = {}
) =>
  useMutation({
    mutationFn: async ({ repositoryId, body }) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/repositories/:repositoryId/sboms', { repositoryId }),
        options: {
          method: 'POST',
          body: createSbomFormData(body),
          // Override the wrapper default content type by setting the header
          // Allow the browser to handle the content type
          headers: {
            'X-Requested-With': 'JavaScript',
          },
        },
        schema: SbomResponseSchema,
        successStatusCodes: [201],
        deserializeFn: (response: Response) => response.json(),
      }),
    ...options,
  })

export const usePostAnalyzeScan = (
  options: Omit<
    UseMutationOptions<AnalysisResponse, DefaultError, { scanId: string; findingIds?: string[] }>,
    'mutationFn' | 'mutationKey'
  > = {}
) => {
  const { onSuccess, ...restOptions } = options

  const _onSuccess = useCallback<Exclude<typeof onSuccess, undefined>>(
    (data, variables, onMutateResult, context) => {
      const scanId = data._embedded?.scan?.id
      if (scanId) {
        context.client.invalidateQueries({ queryKey: getScanQueryKey({ id: scanId }) })
        context.client.invalidateQueries({ queryKey: getScanFindingsQueryKeyPrefix(scanId) })
      }

      context.client.invalidateQueries({
        queryKey: getScansQueryKey({ page: { number: 0, size: 1 }, filters: {} }),
      })

      context.client.invalidateQueries({
        queryKey: getAnalysesQueryKey(),
        refetchType: 'active',
      })

      onSuccess?.(data, variables, onMutateResult, context)
    },
    [onSuccess]
  )

  return useMutation({
    mutationFn: async ({ scanId, findingIds }: { scanId: string; findingIds?: string[] }) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/scans/:scanId/analyses', { scanId }),
        options: {
          method: 'POST',
          headers: {
            'X-Requested-With': 'JavaScript',
            ...(findingIds && { 'Content-Type': 'application/json' }),
          },
          ...(findingIds && { body: JSON.stringify({ finding_ids: findingIds }) }),
        },
        schema: AnalysisResponseSchema,
        successStatusCodes: [201],
        deserializeFn: (response: Response) => response.json(),
      }),
    onSuccess: _onSuccess,
    ...restOptions,
  })
}

export const useDeleteAnalysis = ({
  onSuccess,
  onError,
  retry,
}: {
  onSuccess?: OnSuccess<unknown, { analysisId: string }>
  onError?: OnError<unknown, { analysisId: string }>
  retry?: number
} = {}) =>
  useMutation({
    mutationFn: async ({ analysisId }) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/analyses/:analysisId', { analysisId }),
        options: { method: 'DELETE' },
        successStatusCodes: [204, 404],
        schema: z.unknown(),
      }),
    onSuccess,
    onError,
    retry,
  })

export const usePostAnalysisCancellation = (
  options: Omit<
    UseMutationOptions<AnalysisCancellationResponse, DefaultError, { scanId: string }>,
    'mutationFn' | 'mutationKey'
  > = {}
) => {
  const { onSuccess, ...restOptions } = options

  const _onSuccess = useCallback<Exclude<typeof onSuccess, undefined>>(
    (data, variables, onMutateResult, context) => {
      context.client.invalidateQueries({ queryKey: getScanQueryKey({ id: variables.scanId }) })
      context.client.invalidateQueries({ queryKey: getScanFindingsQueryKeyPrefix(variables.scanId) })
      context.client.invalidateQueries({
        queryKey: getAnalysesQueryKey(),
        refetchType: 'active',
      })

      onSuccess?.(data, variables, onMutateResult, context)
    },
    [onSuccess]
  )

  return useMutation({
    mutationFn: async ({ scanId }: { scanId: string }) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/scans/:scanId/analysis-cancellations', { scanId }),
        options: {
          method: 'POST',
          headers: {
            'X-Requested-With': 'JavaScript',
          },
        },
        schema: AnalysisCancellationResponseSchema,
        successStatusCodes: [201],
        deserializeFn: (response: Response) => response.json(),
      }),
    onSuccess: _onSuccess,
    ...restOptions,
  })
}

export const usePostAddRepository = ({
  onSuccess,
  onError,
}: { onSuccess?: OnSuccess<unknown, AddRepositoryRequest>; onError?: OnError<unknown, AddRepositoryRequest> } = {}) => {
  const _onSuccess = useCallback<Exclude<typeof onSuccess, undefined>>(
    (data, variables, onMutateResult, context) => {
      context.client.invalidateQueries({ queryKey: getRepositoriesQueryKey() })
      onSuccess?.(data, variables, onMutateResult, context)
    },
    [onSuccess]
  )

  return useMutation({
    mutationFn: async params =>
      fetchWrapperMutation({
        path: '/api/v1/repositories',
        options: {
          method: 'POST',
          body: JSON.stringify(AddRepositoryRequestSchema.parse(params)),
        },
        successStatusCodes: [201],
        schema: z.unknown(),
      }),
    onSuccess: _onSuccess,
    onError,
  })
}

type PostScanPatchVariables = {
  createScanPatchRequest: PatchesCreatePatchRequest
}

type GetPostScanPatchMutationOptionsArgs = {
  scanId?: string
} & Omit<UseMutationOptions<unknown, DefaultError, PostScanPatchVariables>, 'mutationKey' | 'mutationFn'>

const getPostScanPatchMutationOptions = ({ scanId, ...rest }: GetPostScanPatchMutationOptionsArgs) =>
  mutationOptions({
    mutationKey: ['post-scan-patch', scanId] as const,
    mutationFn: async ({ createScanPatchRequest }: PostScanPatchVariables) =>
      fetchWrapperMutation({
        path: `/api/v1/patches`,
        options: {
          method: 'POST',
          body: JSON.stringify(PatchesCreatePatchRequestSchema.parse(createScanPatchRequest)),
        },
        successStatusCodes: [201],
        schema: z.unknown(),
      }),
    ...rest,
  })

export const usePostScanPatch = ({ scanId, onSuccess, ...rest }: GetPostScanPatchMutationOptionsArgs) => {
  const _onSuccess = useCallback<NonNullable<typeof onSuccess>>(
    (data, variables, onMutateResult, context) => {
      if (scanId) {
        context.client.invalidateQueries({ queryKey: getScanFindingsQueryKeyPrefix(scanId) })
        context.client.invalidateQueries({ queryKey: getScanFindingQueryKeyPrefix(scanId) })
        context.client.invalidateQueries({ queryKey: getScanFindingResultsQueryKeyPrefix(scanId) })
      }
      onSuccess?.(data, variables, onMutateResult, context)
    },
    [onSuccess, scanId]
  )

  return useMutation(getPostScanPatchMutationOptions({ scanId, onSuccess: _onSuccess, ...rest }))
}

export const useIsPostScanPatchMutating = (scanId?: string) =>
  useIsMutating({ mutationKey: getPostScanPatchMutationOptions({ scanId }).mutationKey }) > 0

export const usePostCreateWorkflow = ({
  onSuccess,
  onError,
}: {
  onSuccess?: OnSuccess<unknown, { repositoryId: string; workflow: CreateWorkflowRequest }>
  onError?: OnError<unknown, { repositoryId: string; workflow: CreateWorkflowRequest }>
} = {}) => {
  const _onSuccess = useCallback<Exclude<typeof onSuccess, undefined>>(
    (data, variables, onMutateResult, context) => {
      context.client.invalidateQueries({ queryKey: getGetWorkflowsQueryKey({ repositoryId: variables.repositoryId }) })
      onSuccess?.(data, variables, onMutateResult, context)
    },
    [onSuccess]
  )

  return useMutation({
    mutationFn: async ({ repositoryId, workflow }) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/repositories/:repositoryId/workflows', { repositoryId }),
        options: {
          method: 'POST',
          body: JSON.stringify(CreateWorkflowRequestSchema.parse(workflow)),
        },
        successStatusCodes: [201],
        schema: z.unknown(),
      }),
    onSuccess: _onSuccess,
    onError,
  })
}

const getPutUpdateWorkflowMutationKey = () => ['updateWorkflow'] as const

export const useIsPutUpdateWorkflowMutating = () =>
  useIsMutating({ mutationKey: getPutUpdateWorkflowMutationKey() }) > 0

export const usePutUpdateWorkflow = ({
  onSuccess,
  ...rest
}: Omit<
  UseMutationOptions<unknown, DefaultError, { workflowId: string; update: UpdateWorkflowRequest }>,
  'mutationKey' | 'mutationFn'
> = {}) => {
  const _onSuccess = useCallback<NonNullable<typeof onSuccess>>(
    (data, variables, onMutateResult, context) => {
      context.client.invalidateQueries({ queryKey: getGetWorkflowsQueryKey() })
      onSuccess?.(data, variables, onMutateResult, context)
    },
    [onSuccess]
  )

  return useMutation({
    mutationKey: getPutUpdateWorkflowMutationKey(),
    mutationFn: async ({ workflowId, update }) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/workflows/:workflowId', { workflowId }),
        options: {
          method: 'PUT',
          body: JSON.stringify(UpdateWorkflowRequestSchema.parse(update)),
        },
        successStatusCodes: [200],
        // The response isn't consumed — success is handled via query invalidation on onSuccess.
        // Matches usePostCreateWorkflow. A strict schema here would reject backend responses with
        // shape drift and surface a failure toast despite the 200.
        schema: z.unknown(),
      }),
    onSuccess: _onSuccess,
    ...rest,
  })
}

export const useDeleteWorkflow = ({
  onSuccess,
  onError,
}: {
  onSuccess?: OnSuccess<unknown, { workflowId: string }>
  onError?: OnError<unknown, { workflowId: string }>
} = {}) => {
  const _onSuccess = useCallback<Exclude<typeof onSuccess, undefined>>(
    (data, variables, onMutateResult, context) => {
      context.client.invalidateQueries({ queryKey: getGetWorkflowsQueryKey() })
      onSuccess?.(data, variables, onMutateResult, context)
    },
    [onSuccess]
  )

  return useMutation({
    mutationFn: async ({ workflowId }) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/workflows/:workflowId', { workflowId }),
        options: {
          method: 'DELETE',
          headers: {
            'X-Requested-With': 'JavaScript',
          },
        },
        successStatusCodes: [204],
        schema: z.unknown(),
      }),
    onSuccess: _onSuccess,
    onError,
  })
}

export const useDeleteRepository = ({
  onSuccess,
  onError,
}: {
  onSuccess?: UseMutationOptions<unknown, DefaultError, { repositoryId: string }>['onSuccess']
  onError?: UseMutationOptions<unknown, DefaultError, { repositoryId: string }>['onError']
}) => {
  const _onSuccess = useCallback(
    ((data, variables, onMutateResult, context) => {
      context.client.invalidateQueries({ queryKey: getRepositoriesQueryKey() })
      context.client.invalidateQueries({ queryKey: getRepositoryQueryKey(variables.repositoryId) })
      onSuccess?.(data, variables, onMutateResult, context)
    }) satisfies Exclude<typeof onSuccess, undefined>,
    [onSuccess]
  )

  return useMutation({
    mutationFn: async ({ repositoryId }: { repositoryId: string }) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/repositories/:repositoryId', { repositoryId }),
        options: {
          method: 'DELETE',
          headers: {
            'X-Requested-With': 'JavaScript',
          },
        },
        successStatusCodes: [204],
        schema: z.unknown(),
      }),
    onSuccess: _onSuccess,
    onError,
    retry: 0,
  })
}

export const useDeleteScan = ({
  onSuccess,
  onError,
  ...rest
}: Omit<UseMutationOptions<void, DefaultError, { scanId: string }>, 'mutationKey' | 'mutationFn'>) => {
  const _onSuccess = useCallback(
    ((data, variables, onMutateResult, context) => {
      context.client.setQueriesData<ScanAggregateResponse>({ queryKey: ['scans'] }, oldData => {
        if (!oldData?._embedded?.items) {
          return oldData
        }
        return {
          ...oldData,
          total: oldData.total - 1,
          _embedded: {
            items: oldData._embedded.items.filter(scan => scan.id !== variables.scanId),
          },
        }
      })
      context.client.invalidateQueries({ queryKey: ['scans'] })
      onSuccess?.(data, variables, onMutateResult, context)
    }) satisfies NonNullable<typeof onSuccess>,
    [onSuccess]
  )

  return useMutation({
    mutationFn: async ({ scanId }) => {
      await fetchWrapperMutation({
        path: buildPath('/api/v1/scans/:scanId', { scanId }),
        options: {
          method: 'DELETE',
          headers: {
            'X-Requested-With': 'JavaScript',
          },
        },
        successStatusCodes: [204],
        schema: z.unknown(),
      })
    },
    onSuccess: _onSuccess,
    onError,
    retry: 0,
    ...rest,
  })
}

export const usePostRepositoryIntegration = ({
  onSuccess,
  onError,
}: Pick<
  UseMutationOptions<unknown, ApiError, { repositoryId: string; body: CreateRepositoryIntegrationRequest }>,
  'onSuccess' | 'onError'
> = {}) => {
  const _onSuccess = useCallback(
    ((data, variables, onMutateResult, context) => {
      context.client.invalidateQueries({
        queryKey: getRepositoryIntegrationsQueryKey({ repositoryId: variables.repositoryId }),
      })
      onSuccess?.(data, variables, onMutateResult, context)
    }) satisfies Exclude<typeof onSuccess, undefined>,
    [onSuccess]
  )

  return useMutation({
    mutationFn: async ({ repositoryId, body }: { repositoryId: string; body: CreateRepositoryIntegrationRequest }) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/repositories/:repositoryId/integrations', { repositoryId }),
        options: {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'JavaScript',
          },
          body: JSON.stringify(CreateRepositoryIntegrationRequestSchema.parse(body)),
        },
        successStatusCodes: [201],
        schema: z.unknown(),
      }),
    onSuccess: _onSuccess,
    onError,
  })
}

/**
 * A create/update body for a tool whose Create/Update variant has not shipped in the spec yet. The
 * Available catalog gates those tools out of setup via `getIsIntegrationTypeSupportedByApi`, the same
 * predicate that decides below whether a body is parsed — so a variant shipping upstream opens the
 * catalog and turns on schema parsing together, and neither can be enabled without the other.
 *
 * The extra fields are `string | undefined` rather than `unknown`: every one is a credential or a
 * URI collected from a text input. `unknown` made this arm match any object at all, which erased the
 * typed arm of the union it sits in and turned a compile error into a runtime `parse` throw.
 */
export type AheadOfSpecIntegrationBody = {
  type: Exclude<IntegrationType, WritableIntegrationType>
  name: string
} & Record<string, string | undefined>

export type PostIntegrationVariables = { body: CreateIntegrationRequest | AheadOfSpecIntegrationBody }

export const usePostIntegration = ({
  onSuccess,
  ...options
}: Omit<UseMutationOptions<unknown, DefaultError, PostIntegrationVariables>, 'mutationFn'> = {}) => {
  const _onSuccess = useCallback(
    ((data, variables, onMutateResult, context) => {
      context.client.invalidateQueries({ queryKey: ['integrations'] })
      onSuccess?.(data, variables, onMutateResult, context)
    }) satisfies Exclude<typeof onSuccess, undefined>,
    [onSuccess]
  )

  return useMutation({
    mutationFn: async ({ body }: PostIntegrationVariables) =>
      fetchWrapperMutation({
        path: '/api/v1/integrations',
        options: {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'JavaScript',
          },
          body: JSON.stringify(
            getIsIntegrationTypeSupportedByApi(body.type) ? CreateIntegrationRequestSchema.parse(body) : body
          ),
        },
        successStatusCodes: [201],
        schema: z.unknown(),
      }),
    onSuccess: _onSuccess,
    ...options,
  })
}

export type PutIntegrationVariables = {
  integrationId: string
  body: UpdateIntegrationRequest | AheadOfSpecIntegrationBody
}

export const usePutIntegration = ({
  onSuccess,
  ...options
}: Omit<UseMutationOptions<unknown, DefaultError, PutIntegrationVariables>, 'mutationFn'> = {}) => {
  const _onSuccess = useCallback(
    ((data, variables, onMutateResult, context) => {
      context.client.invalidateQueries({ queryKey: ['integrations'] })
      onSuccess?.(data, variables, onMutateResult, context)
    }) satisfies Exclude<typeof onSuccess, undefined>,
    [onSuccess]
  )

  return useMutation({
    mutationFn: async ({ integrationId, body }: PutIntegrationVariables) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/integrations/:integrationId', { integrationId }),
        options: {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'JavaScript',
          },
          body: JSON.stringify(
            getIsIntegrationTypeSupportedByApi(body.type) ? UpdateIntegrationRequestSchema.parse(body) : body
          ),
        },
        successStatusCodes: [200],
        schema: z.unknown(),
      }),
    onSuccess: _onSuccess,
    ...options,
  })
}

export type DeleteIntegrationVariables = { integrationId: string }

export const useDeleteIntegration = ({
  onSuccess,
  ...options
}: Omit<UseMutationOptions<unknown, DefaultError, DeleteIntegrationVariables>, 'mutationFn'> = {}) => {
  const _onSuccess = useCallback(
    ((data, variables, onMutateResult, context) => {
      context.client.invalidateQueries({ queryKey: ['integrations'] })
      onSuccess?.(data, variables, onMutateResult, context)
    }) satisfies Exclude<typeof onSuccess, undefined>,
    [onSuccess]
  )

  return useMutation({
    mutationFn: async ({ integrationId }: DeleteIntegrationVariables) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/integrations/:integrationId', { integrationId }),
        options: {
          method: 'DELETE',
          headers: {
            'X-Requested-With': 'JavaScript',
          },
        },
        successStatusCodes: [204],
        schema: z.unknown(),
      }),
    onSuccess: _onSuccess,
    ...options,
  })
}

export const useDeleteRepositoryIntegration = ({
  onSuccess,
  onError,
}: Pick<
  UseMutationOptions<unknown, ApiError, { repositoryId: string; integrationId: string }>,
  'onSuccess' | 'onError'
> = {}) => {
  const _onSuccess = useCallback(
    ((data, variables, onMutateResult, context) => {
      context.client.invalidateQueries({
        queryKey: getRepositoryIntegrationsQueryKey({ repositoryId: variables.repositoryId }),
      })
      onSuccess?.(data, variables, onMutateResult, context)
    }) satisfies Exclude<typeof onSuccess, undefined>,
    [onSuccess]
  )

  return useMutation({
    mutationFn: async ({ repositoryId, integrationId }: { repositoryId: string; integrationId: string }) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/repositories/:repositoryId/integrations/:integrationId', {
          repositoryId,
          integrationId,
        }),
        options: {
          method: 'DELETE',
          headers: {
            'X-Requested-With': 'JavaScript',
          },
        },
        successStatusCodes: [204],
        schema: z.unknown(),
      }),
    onSuccess: _onSuccess,
    onError,
  })
}

// #endregion mutations

// #region: useQueryWithLinks utility

export type LinkConfig<T> = {
  key: string // e.g. '_links.repository.href' or '_embedded.items._links.repository.href'
  schema: z.ZodType<T>
}

/**
 * Utility for querying an API and its HAL links
 *
 * @param baseQuery - The base query options
 * @param linkConfigs - The link configurations
 */
export function useQueryWithLinks<
  TData extends { _embedded: { items: unknown[] } },
  T1,
  TError = Error,
  TQueryKey extends QueryKey = QueryKey,
>(
  baseQuery: UseQueryOptions<TData, TError, TData, TQueryKey>,
  linkConfigs: [LinkConfig<T1>]
): { baseResult: UseQueryResult<TData>; linkedResults: [UseQueryResult<T1>[]] }

export function useQueryWithLinks<
  TData extends { _embedded: { items: unknown[] } },
  T1,
  T2,
  TError = Error,
  TQueryKey extends QueryKey = QueryKey,
>(
  baseQuery: UseQueryOptions<TData, TError, TData, TQueryKey>,
  linkConfigs: [LinkConfig<T1>, LinkConfig<T2>]
): { baseResult: UseQueryResult<TData>; linkedResults: [UseQueryResult<T1>[], UseQueryResult<T2>[]] }

export function useQueryWithLinks<
  TData extends { _embedded: { items: unknown[] } },
  T1,
  T2,
  T3,
  TError = Error,
  TQueryKey extends QueryKey = QueryKey,
>(
  baseQuery: UseQueryOptions<TData, TError, TData, TQueryKey>,
  linkConfigs: [LinkConfig<T1>, LinkConfig<T2>, LinkConfig<T3>]
): {
  baseResult: UseQueryResult<TData>
  linkedResults: [UseQueryResult<T1>[], UseQueryResult<T2>[], UseQueryResult<T3>[]]
}

export function useQueryWithLinks<
  TData extends { _embedded: { items: unknown[] } },
  TError = Error,
  TQueryKey extends QueryKey = QueryKey,
>(baseQuery: UseQueryOptions<TData, TError, TData, TQueryKey>, linkConfigs: LinkConfig<unknown>[]) {
  const baseResult = useQuery(baseQuery)

  const queryGroups = useMemo(
    () =>
      baseResult.data && linkConfigs.length > 0
        ? createLinksQueryConfigs(baseQuery, baseResult, linkConfigs)
        : linkConfigs.map(() => [] as UseQueryOptions[]),
    [baseResult.data, baseQuery.queryKey, linkConfigs]
  )

  const flatQueries = useMemo(() => queryGroups.flat(), [queryGroups])
  const flatResults = useQueries({ queries: flatQueries })

  let offset = 0
  const linkedResults = queryGroups.map(group => {
    const chunk = flatResults.slice(offset, offset + group.length)
    offset += group.length
    return chunk
  })

  return { baseResult, linkedResults }
}

const createLinksQueryConfigs = <
  TData extends { _embedded: { items: unknown[] } },
  TError = Error,
  TQueryKey extends QueryKey = QueryKey,
>(
  baseQuery: UseQueryOptions<TData, TError, TData, TQueryKey>,
  baseResult: UseQueryResult<TData, TError>,
  linkConfigs: LinkConfig<unknown>[]
) =>
  linkConfigs.map(config =>
    match(config.key)
      .when(
        key => key.includes('_embedded.items'),
        () => {
          const items = baseResult.data?._embedded?.items ?? []
          return items.map(item =>
            createItemLinkQueryConfig(
              baseQuery.queryKey,
              getStringAtPath(item, config.key.replace(/^_embedded\.items\./, '')),
              config
            )
          )
        }
      )
      .otherwise(() => [
        createLinkQueryConfig(baseQuery.queryKey, getStringAtPath(baseResult.data, config.key), config),
      ])
  )

const createItemLinkQueryConfig = <T>(
  baseQueryKey: readonly unknown[],
  path: string,
  config: LinkConfig<T>
): UseQueryOptions<T> => ({
  queryKey: [...baseQueryKey, `link:${config.key}`, `item:${path}`, config.schema],
  queryFn: () => fetchWrapperQuery({ path, schema: config.schema }),
  enabled: !!path,
})

const createLinkQueryConfig = <T>(
  baseQueryKey: readonly unknown[],
  path: string,
  config: LinkConfig<T>
): UseQueryOptions<T> => ({
  queryKey: [...baseQueryKey, `link:${config.key}`, path, config.schema],
  queryFn: () => fetchWrapperQuery({ path, schema: config.schema }),
  enabled: !!path,
})

const getStringAtPath = (object: unknown, path: string) => {
  const result = _.get(object, path) as unknown
  invariantType(result, 'string', 'result')

  return result
}

// #endregion

// #region: useGetAllPages utility

type PageOptions = {
  number: number
  size: number
}

type GetAllPagesOptions<TData extends PaginatedResponse, TError = Error, TQueryKey extends QueryKey = QueryKey> = {
  getPageQueryOptions: (pageOptions: PageOptions) => UseQueryOptions<TData, TError, TData, TQueryKey>
  pageSize?: number
}

/**
 * **Utility for fetching all pages**
 *
 * @param options.pageToQueryOptions - A function that returns a query options for a given page
 * @returns A hook that fetches all pages of a paginated resource
 *
 * **Parallel queries considerations**
 *
 * Currently all pages are fetched in parallel.
 *
 * In the case of say 1000 analyses with 100 per page, this will fetch 10 queries in parallel which is fine for our database load constraints (few users per enterprise server).
 *
 * In the future may need to limit the number of parallel queries, see this issue for implementation reference: https://github.com/TanStack/query/issues/1000.
 */
export const useGetAllPages = <TData extends PaginatedResponse, TError = Error, TQueryKey extends QueryKey = QueryKey>({
  getPageQueryOptions,
  pageSize = 100,
}: GetAllPagesOptions<TData, TError, TQueryKey>): UseQueryResult<TData, TError>[] => {
  const queryResult = useQuery(getPageQueryOptions({ number: 0, size: pageSize }))
  const queries = calculateAllPages(queryResult.data).map(getPageQueryOptions)
  return useQueries({ queries })
}

export const calculateAllPages = (data?: PaginatedResponse): PageOptions[] => {
  if (!data) return []
  const total = data.total
  const size = data.page.size
  const pageCount = Math.ceil(total / size)
  return Array.from({ length: pageCount }, (_, i) => ({ number: i, size }))
}

// #endregion

// #region: preferences

export type OrganizationPreferencesWithEtag = {
  data: OrganizationPreferencesResponse | null
  etag: string | null
}

type GetOrganizationPreferencesOptionsInput = {
  organizationId?: string
}

const getOrganizationPreferencesOptions = ({
  organizationId = 'default-organization',
}: GetOrganizationPreferencesOptionsInput = {}) =>
  queryOptions({
    queryKey: ['organization-preferences', organizationId],
    queryFn: async (): Promise<OrganizationPreferencesWithEtag> => {
      const path = buildPath('/api/v1/organizations/:organizationId/preferences', { organizationId })
      const response = await fetch(path, {
        headers: {
          'X-Requested-With': 'JavaScript',
          Accept: 'application/hal+json, application/json',
        },
      })

      const etag = response.headers.get('ETag')

      if (response.status === 204) {
        return { data: null, etag: null }
      }

      if (response.status !== 200) {
        throw new ApiError(response.status, await response.text(), path)
      }

      const data = await logZodError<OrganizationPreferencesResponse>(
        path,
        OrganizationPreferencesResponseSchema,
        async () => OrganizationPreferencesResponseSchema.parse(await response.json())
      )

      return { data, etag: etag || null }
    },
  })

export const useSuspenseGetOrganizationPreferences = (input?: GetOrganizationPreferencesOptionsInput) =>
  useSuspenseQuery(getOrganizationPreferencesOptions(input))

export const usePutOrganizationPreferences = ({
  organizationId = 'default-organization',
  onSuccess,
  onError,
  ...options
}: {
  organizationId?: string
} & Omit<UseMutationOptions<void, ApiError, { contentMarkdown: string; etag: string | null }>, 'mutationFn'> = {}) => {
  const _onSuccess = useCallback<Exclude<typeof onSuccess, undefined>>(
    (data, variables, onMutateResult, context) => {
      context.client.setQueryData(
        ['organization-preferences', organizationId],
        (old: OrganizationPreferencesWithEtag | undefined) =>
          old ? { ...old, data: old.data ? { ...old.data, content: variables.contentMarkdown } : old.data } : old
      )
      context.client.invalidateQueries({ queryKey: ['organization-preferences'] })
      onSuccess?.(data, variables, onMutateResult, context)
    },
    [organizationId, onSuccess]
  )

  // A 403 is the server telling us the cached authorization is stale, so drop `['current-user']`
  // rather than keep offering an Edit button the API will refuse. Without this the component
  // re-offers the control off a cache react-query has no reason to consider wrong.
  const _onError = useCallback<Exclude<typeof onError, undefined>>(
    (error, variables, onMutateResult, context) => {
      if (error.status === 403) {
        context.client.invalidateQueries({ queryKey: ['current-user'] })
      }
      onError?.(error, variables, onMutateResult, context)
    },
    [onError]
  )

  return useMutation({
    mutationFn: async ({ contentMarkdown, etag }: { contentMarkdown: string; etag: string | null }) => {
      const path = buildPath('/api/v1/organizations/:organizationId/preferences', { organizationId })
      const headers: Record<string, string> = {
        'X-Requested-With': 'JavaScript',
        Accept: 'application/hal+json, application/json',
        'Content-Type': 'application/json',
      }

      if (etag) {
        headers['If-Match'] = etag
      } else {
        headers['If-None-Match'] = '*'
      }

      const response = await fetch(path, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ content: contentMarkdown }),
      })

      if (![200, 204].includes(response.status)) {
        throw new ApiError(response.status, await response.text(), path)
      }
    },
    onSuccess: _onSuccess,
    onError: _onError,
    ...options,
  })
}

export const useGetAnalysisPreferences = ({
  analysisId,
  enabled = true,
  ...rest
}: {
  analysisId?: string
  enabled?: boolean
} & Omit<UseQueryOptions<string | null>, 'queryKey' | 'queryFn'> = {}) => {
  return useQuery({
    queryKey: ['analysis-preferences', analysisId],
    // This endpoint returns text/markdown (not JSON), so fetchWrapperQuery cannot be used —
    // response.text() is required instead of response.json().
    queryFn: async () => {
      const path = buildPath('/api/v1/analyses/:analysisId/preferences', { analysisId: analysisId ?? '' })
      const response = await fetch(path, {
        headers: { 'X-Requested-With': 'JavaScript', Accept: 'text/markdown; variant=GFM' },
      })
      if (response.status === 204) return null
      if (response.status === 404) return null
      if (response.status !== 200) throw new ApiError(response.status, await response.text(), path)
      return await response.text()
    },
    enabled: !!analysisId && enabled,
    ...rest,
  })
}

// #endregion

// #region SSE Hooks

const ACTIVITY_ENDPOINT_BY_TYPE = {
  triage: '/api/v1/triage-results/:resultId/activity',
  fix: '/api/v1/fix-results/:resultId/activity',
  sca: '/api/v1/sca-results/:resultId/activity',
} as const

export const useGetResultActivitySSE = (
  resultId: string | undefined,
  resultType: 'triage' | 'fix' | 'sca'
): SSEStream<AnalysisStreamEvent> => {
  const url = resultId ? buildPath(ACTIVITY_ENDPOINT_BY_TYPE[resultType], { resultId }) : undefined

  return useSSEStream({
    url,
    schema: AnalysisStreamEventSchema,
    isFinished: event => event.type === 'data-progress-finding-complete',
    onEvent: event => {
      if (event.type === 'error') {
        console.warn('SSE error event received:', event.content)
      }
      if (event.type === 'workflow' && event.content.status === 'error') {
        console.warn('SSE workflow error:', event.content.error ?? event.content.description)
      }
    },
  })
}

/**
 * Subscribes to the SSE phase stream for a single support bundle. Passing `undefined` keeps the
 * hook idle (no connection opened); pass the bundle id returned by `usePostSupportBundle`'s 202
 * response to start streaming. The stream closes when the bundle reaches a terminal phase.
 */
export const useSupportBundleStream = (bundleId: string | undefined): SSEStream<SupportBundlePhaseEvent> =>
  useSSEStream({
    url: bundleId ? buildPath('/api/v1/support-bundles/:bundleId', { bundleId }) : undefined,
    schema: SupportBundlePhaseEventSchema,
    isFinished: event => event.phase === 'succeeded' || event.phase === 'failed',
  })

// #endregion

// #region Feedback Hooks

type FeedbackSubmissionVariables = { resultId: string; rating: FeedbackRating; text: string }

export const usePostTriageFeedbackSubmission = ({
  onSuccess,
  ...options
}: Omit<
  UseMutationOptions<FeedbackSubmissionResponse, DefaultError, FeedbackSubmissionVariables>,
  'mutationFn' | 'mutationKey'
> = {}) => {
  const _onSuccess = useCallback<Exclude<typeof onSuccess, undefined>>(
    (data, variables, onMutateResult, context) => {
      invalidateGetTriageFeedbackSubmissions(context.client, { resultId: variables.resultId })
      onSuccess?.(data, variables, onMutateResult, context)
    },
    [onSuccess]
  )

  return useMutation({
    mutationFn: ({ resultId, rating, text }) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/triage-results/:resultId/feedback-submissions', { resultId }),
        options: { method: 'POST', body: JSON.stringify({ rating, text }) },
        schema: FeedbackSubmissionResponseSchema,
        successStatusCodes: [201],
        deserializeFn: async response => (await response.json()) as unknown,
      }),
    onSuccess: _onSuccess,
    ...options,
  })
}

export const usePostFixFeedbackSubmission = ({
  onSuccess,
  ...options
}: Omit<
  UseMutationOptions<FeedbackSubmissionResponse, DefaultError, FeedbackSubmissionVariables>,
  'mutationFn' | 'mutationKey'
> = {}) => {
  const _onSuccess = useCallback<Exclude<typeof onSuccess, undefined>>(
    (data, variables, onMutateResult, context) => {
      invalidateGetFixFeedbackSubmissions(context.client, { resultId: variables.resultId })
      onSuccess?.(data, variables, onMutateResult, context)
    },
    [onSuccess]
  )

  return useMutation({
    mutationFn: ({ resultId, rating, text }) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/fix-results/:resultId/feedback-submissions', { resultId }),
        options: { method: 'POST', body: JSON.stringify({ rating, text }) },
        schema: FeedbackSubmissionResponseSchema,
        successStatusCodes: [201],
        deserializeFn: async response => (await response.json()) as unknown,
      }),
    onSuccess: _onSuccess,
    ...options,
  })
}

export const usePostScaFeedbackSubmission = ({
  onSuccess,
  ...options
}: Omit<
  UseMutationOptions<FeedbackSubmissionResponse, DefaultError, FeedbackSubmissionVariables>,
  'mutationFn' | 'mutationKey'
> = {}) => {
  const _onSuccess = useCallback<Exclude<typeof onSuccess, undefined>>(
    (data, variables, onMutateResult, context) => {
      invalidateGetScaFeedbackSubmissions(context.client, { resultId: variables.resultId })
      onSuccess?.(data, variables, onMutateResult, context)
    },
    [onSuccess]
  )

  return useMutation({
    mutationFn: ({ resultId, rating, text }) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/sca-results/:resultId/feedback-submissions', { resultId }),
        options: { method: 'POST', body: JSON.stringify({ rating, text }) },
        schema: FeedbackSubmissionResponseSchema,
        successStatusCodes: [201],
        deserializeFn: async response => (await response.json()) as unknown,
      }),
    onSuccess: _onSuccess,
    ...options,
  })
}

const getTriageFeedbackSubmissionsOptions = ({
  resultId,
  enabled = true,
  ...rest
}: Omit<UseQueryOptions<FeedbackSubmissionPaginatedResponse, ApiError>, 'queryKey' | 'queryFn'> & {
  resultId: string
}) =>
  queryOptions({
    queryKey: ['triage-feedback-submissions', resultId],
    queryFn: () =>
      fetchWrapperQuery({
        path: buildPath('/api/v1/triage-results/:resultId/feedback-submissions', { resultId }),
        schema: FeedbackSubmissionPaginatedResponseSchema,
      }),
    enabled: enabled && !!resultId,
    ...rest,
  })

export const useGetTriageFeedbackSubmissions = (options: Parameters<typeof getTriageFeedbackSubmissionsOptions>[0]) =>
  useQuery(getTriageFeedbackSubmissionsOptions(options))

export const invalidateGetTriageFeedbackSubmissions = (
  client: QueryClient,
  input: Parameters<typeof getTriageFeedbackSubmissionsOptions>[0]
) => client.invalidateQueries({ queryKey: getTriageFeedbackSubmissionsOptions(input).queryKey })

const getFixFeedbackSubmissionsOptions = ({
  resultId,
  enabled = true,
  ...rest
}: Omit<UseQueryOptions<FeedbackSubmissionPaginatedResponse, ApiError>, 'queryKey' | 'queryFn'> & {
  resultId: string
}) =>
  queryOptions({
    queryKey: ['fix-feedback-submissions', resultId],
    queryFn: () =>
      fetchWrapperQuery({
        path: buildPath('/api/v1/fix-results/:resultId/feedback-submissions', { resultId }),
        schema: FeedbackSubmissionPaginatedResponseSchema,
      }),
    enabled: enabled && !!resultId,
    ...rest,
  })

export const useGetFixFeedbackSubmissions = (options: Parameters<typeof getFixFeedbackSubmissionsOptions>[0]) =>
  useQuery(getFixFeedbackSubmissionsOptions(options))

export const invalidateGetFixFeedbackSubmissions = (
  client: QueryClient,
  input: Parameters<typeof getFixFeedbackSubmissionsOptions>[0]
) => client.invalidateQueries({ queryKey: getFixFeedbackSubmissionsOptions(input).queryKey })

const getScaFeedbackSubmissionsOptions = ({
  resultId,
  enabled = true,
  ...rest
}: Omit<UseQueryOptions<FeedbackSubmissionPaginatedResponse, ApiError>, 'queryKey' | 'queryFn'> & {
  resultId: string
}) =>
  queryOptions({
    queryKey: ['sca-feedback-submissions', resultId],
    queryFn: () =>
      fetchWrapperQuery({
        path: buildPath('/api/v1/sca-results/:resultId/feedback-submissions', { resultId }),
        schema: FeedbackSubmissionPaginatedResponseSchema,
      }),
    enabled: enabled && !!resultId,
    ...rest,
  })

export const useGetScaFeedbackSubmissions = (options: Parameters<typeof getScaFeedbackSubmissionsOptions>[0]) =>
  useQuery(getScaFeedbackSubmissionsOptions(options))

export const invalidateGetScaFeedbackSubmissions = (
  client: QueryClient,
  input: Parameters<typeof getScaFeedbackSubmissionsOptions>[0]
) => client.invalidateQueries({ queryKey: getScaFeedbackSubmissionsOptions(input).queryKey })

// #endregion

// #region Push

type PostPushVariables = { triageResultIds: string[] }

export const usePostPush = ({
  onSuccess,
  onError,
}: {
  onSuccess?: OnSuccess<CreatePushResponse, PostPushVariables>
  onError?: OnError<CreatePushResponse, PostPushVariables>
} = {}) =>
  useMutation({
    mutationFn: async (variables: PostPushVariables) => {
      const body = { triage_result_ids: variables.triageResultIds }
      return fetchWrapperMutation({
        path: '/api/v1/pushes',
        options: {
          method: 'POST',
          body: JSON.stringify(body),
        },
        schema: CreatePushResponseSchema,
        successStatusCodes: [202],
        deserializeFn: async response => (await response.json()) as unknown,
      })
    },
    onSuccess,
    onError,
  })

type PolarisStatusLookupVariables = { triageResultIds: string[] }

/**
 * How long a displayed Polaris status is treated as current.
 *
 * Only applies to the read-only surfaces below. The push preflight never reads this cache.
 */
const POLARIS_STATUS_STALE_TIME_MS = 30_000

const POLARIS_STATUSES_QUERY_KEY_ROOT = 'polaris-statuses'

/**
 * Drops every cached Polaris status.
 *
 * A completed push is what changes the status these queries report, so its cache must not outlive
 * one. Exported as a helper rather than as a key prefix so that reason lives here, next to the
 * queries it invalidates, rather than in each component that pushes.
 */
export const invalidateGetPolarisStatuses = (client: QueryClient) =>
  client.invalidateQueries({ queryKey: [POLARIS_STATUSES_QUERY_KEY_ROOT] })

const lookupPolarisStatuses = async ({
  triageResultIds,
  includeAttribution = false,
}: {
  triageResultIds: string[]
  includeAttribution?: boolean
}) =>
  fetchWrapperMutation({
    // Only sent when asking for attribution: the endpoint already defaults it to false.
    path: `/api/v1/pushes/polaris-status${includeAttribution ? `?${new URLSearchParams({ include_attribution: 'true' })}` : ''}`,
    options: {
      method: 'POST',
      body: JSON.stringify({ triage_result_ids: triageResultIds } satisfies CreatePushRequest),
    },
    schema: PolarisStatusLookupResponseSchema,
    successStatusCodes: [200],
    deserializeFn: async response => (await response.json()) as unknown,
  })

/**
 * Reads the live Polaris triage status for the given targets, on demand.
 *
 * A mutation rather than a query because the result gates a write: the push confirmation has to
 * reflect Polaris as it is at push time, and a cached answer would let a status that changed since
 * the page loaded pass unchallenged. Use `useGetPolarisStatuses` for the read-only surfaces where a
 * cached answer is what you want.
 */
export const usePostPolarisStatusLookup = (
  options: Omit<
    UseMutationOptions<PolarisStatusLookupResponse, DefaultError, PolarisStatusLookupVariables>,
    'mutationFn'
  > = {}
) => {
  const queryClient = useQueryClient()
  return useMutation({
    // No attribution: this gates a push on the conflict count, and asking for who dismissed each
    // finding costs a Polaris request per conflict — a bulk push would pay it for nothing.
    mutationFn: async (variables: PolarisStatusLookupVariables) =>
      lookupPolarisStatuses({ triageResultIds: variables.triageResultIds }),
    // This uncached read is the more recent of the two, so anything the cached queries are still
    // showing is superseded by it — including a "match" that has since become a conflict, which is
    // the case the tooltip most needs to stop repeating.
    onSuccess: () => invalidateGetPolarisStatuses(queryClient),
    ...options,
  })
}

/**
 * Query options for displaying Polaris status without intending to push.
 *
 * Deduplicated and cached per set of triage result IDs, so re-reaching the same tooltip does not
 * re-request. IDs asked for one at a time each get their own cache entry, which is what the badge does
 * — it cannot see its siblings. That is affordable only because the badge gates the lookup on hover,
 * so a table of findings has one request in flight rather than one per row. A surface that wants
 * statuses for many targets up front should batch them here and pass the results down instead.
 *
 * Requests attribution, unlike the push preflight: this feeds a tooltip that names who dismissed the
 * finding and when, and it asks about one finding at a time, so the per-conflict cost is one request.
 */
const getPolarisStatusesOptions = ({
  triageResultIds,
  enabled = true,
  ...rest
}: Omit<UseQueryOptions<PolarisStatusLookupResponse, ApiError>, 'queryKey' | 'queryFn'> & {
  triageResultIds: string[]
}) => {
  // Sorted for the request as well as for the key: two callers passing the same ids in a different
  // order share one cache entry, so the payload's order has to be the key's order.
  const sortedTriageResultIds = [...triageResultIds].sort()
  return queryOptions({
    queryKey: [POLARIS_STATUSES_QUERY_KEY_ROOT, sortedTriageResultIds],
    queryFn: () => lookupPolarisStatuses({ triageResultIds: sortedTriageResultIds, includeAttribution: true }),
    enabled: enabled && triageResultIds.length > 0,
    staleTime: POLARIS_STATUS_STALE_TIME_MS,
    ...rest,
  })
}

export const useGetPolarisStatuses = (options: Parameters<typeof getPolarisStatusesOptions>[0]) =>
  useQuery(getPolarisStatusesOptions(options))

const getPushesByIdsOptions = ({ ids, enabled = true }: { ids: string[]; enabled?: boolean }) => {
  const isActive = enabled && ids.length > 0
  return {
    queryKey: ['pushesByIds', [...ids].sort()],
    queryFn: async () => {
      return fetchWrapperQuery({
        path: `/api/v1/pushes?${ids.map(id => `ids=${encodeURIComponent(id)}`).join('&')}`,
        schema: GetPushesResponseSchema,
        deserializeFn: async response => (await response.json()) as unknown,
      })
    },
    enabled: isActive,
    refetchInterval: (query: { state: { data: GetPushesResponse | undefined } }) => {
      if (!isActive) return false as const
      const data = query.state.data
      if (!data) return 1000
      const allTerminal = data.pushes.every(push => push.status === 'SUCCESS' || push.status === 'FAILED')
      return allTerminal ? (false as const) : 1000
    },
  }
}

export const useGetPushesByIds = (input: { ids: string[]; enabled?: boolean }) => useQuery(getPushesByIdsOptions(input))

// #region Generation resources (threat model, exploit chains)

/**
 * Builds the shared hook set for a repository-scoped, on-demand "generation" resource — one that is
 * created by POST, polled/streamed while `processing`, and read back as metadata. Threat Model and
 * Exploit Chains are the same resource shape end to end; this factory is the single source of truth
 * for their polling cadence, query keys, 404→null handling, and SSE wiring so the two can never
 * silently drift.
 *
 * The `/content` sub-resource is deliberately *not* built here: its representation differs per
 * resource (threat-model and finding-overlays return structured JSON, each with its own schema), so
 * every consumer owns a bespoke content hook next to its region below.
 */
const createGenerationResourceHooks = <
  TResponse extends { status: string },
  TStreamEvent extends { status: string },
>(config: {
  /** Query-key prefix, e.g. `repository-threat-model`. */
  queryKeyBase: string
  /** Path segment under `/repositories/:repositoryId/`, e.g. `threat-model`. */
  pathSegment: string
  responseSchema: z.ZodType<TResponse>
  streamEventSchema: z.ZodType<TStreamEvent>
  /** Phase enum options; the SSE event names are these phases plus the terminal statuses. */
  phaseOptions: readonly string[]
  /**
   * Metadata poll interval. Generation takes minutes and SSE is the primary live channel, so the
   * poll is a slow fallback/reconnect mechanism rather than the main progress driver.
   */
  pollIntervalMs?: number
}) => {
  const pollIntervalMs = config.pollIntervalMs ?? 10_000
  // eslint-disable-next-line pixee/no-unescaped-path-params -- pathSegment is a static internal route segment (not user input); buildPath still encodes :repositoryId.
  const basePath = `/api/v1/repositories/:repositoryId/${config.pathSegment}` as const
  // The stream's SSE event names are the phases for progress events plus the terminal statuses;
  // anything else (e.g. periodic `keepalive` events) is dropped silently.
  const streamEventNames = [...config.phaseOptions, 'completed', 'failed'] as const

  const getRefetchInterval = (data: TResponse | null | undefined): number | false =>
    data && isInFlightGenerationStatus(data.status) ? pollIntervalMs : false

  const getOptions = ({
    repositoryId,
    enabled = true,
    ...rest
  }: {
    repositoryId: string
    enabled?: boolean
  } & Omit<UseQueryOptions<TResponse | null, ApiError>, 'queryKey' | 'queryFn'>) =>
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- queryKeyBase uniquely identifies the resource's path + schema (stable per factory instance)
    queryOptions({
      queryKey: [config.queryKeyBase, repositoryId],
      queryFn: () =>
        fetchWrapperQuery({
          path: buildPath(basePath, { repositoryId }),
          schema: z.union([config.responseSchema, z.null()]),
          successStatusCodes: [200, 404],
          // A 404 means the resource has never been generated; surface it as `null` rather than an
          // error. While `processing`, the query polls until the status reaches a terminal state.
          deserializeFn: async (response: Response) => {
            if (response.status === 404) {
              return null
            }

            return (await response.json()) as unknown
          },
        }),
      refetchInterval: query => getRefetchInterval(query.state.data),
      enabled: enabled && !!repositoryId,
      ...rest,
    })

  const useGet = (input: Parameters<typeof getOptions>[0]) => useQuery(getOptions(input))

  const invalidateGet = (client: QueryClient, input: Parameters<typeof getOptions>[0]) =>
    client.invalidateQueries({ queryKey: getOptions(input).queryKey })

  type PostVariables = { repositoryId: string }

  const usePost = ({
    onSuccess,
    onError,
  }: {
    onSuccess?: OnSuccess<TResponse, PostVariables>
    onError?: OnError<TResponse, PostVariables>
  } = {}) =>
    useMutation({
      mutationFn: (variables: PostVariables) =>
        fetchWrapperMutation({
          path: buildPath(basePath, { repositoryId: variables.repositoryId }),
          options: { method: 'POST' },
          schema: config.responseSchema,
          successStatusCodes: [202],
          deserializeFn: async response => (await response.json()) as unknown,
        }),
      onSuccess,
      onError,
    })

  /**
   * Streams progress events for an in-flight generation. The stream replays the persisted event log
   * from the start (idempotently — a stream opened against an already-terminal run replays history
   * plus the terminal event and closes) and live-tails until a terminal event (`completed` or
   * `failed`). Pass `undefined` while no generation is in flight to keep the stream idle. GET polling
   * remains the fallback/reconnect source of truth; a 404 simply marks it unavailable.
   */
  const useStream = (repositoryId: string | undefined): SSEStream<TStreamEvent> =>
    useSSEStream({
      url: repositoryId ? buildPath(`${basePath}/events`, { repositoryId }) : undefined,
      schema: config.streamEventSchema,
      isFinished: event => event.status === 'completed' || event.status === 'failed',
      eventNames: streamEventNames,
    })

  return {
    pollIntervalMs,
    getRefetchInterval,
    getOptions,
    useGet,
    invalidateGet,
    usePost,
    useStream,
  }
}

// #endregion

// #region Threat Model

const threatModelHooks = createGenerationResourceHooks<ThreatModelResponse, ThreatModelStreamEvent>({
  queryKeyBase: 'repository-threat-model',
  pathSegment: 'threat-model',
  responseSchema: ThreatModelResponseSchema,
  streamEventSchema: ThreatModelStreamEventSchema,
  phaseOptions: ThreatModelPhaseSchema.options,
})

export const THREAT_MODEL_POLL_INTERVAL_MS = threatModelHooks.pollIntervalMs
export const getThreatModelRefetchInterval = threatModelHooks.getRefetchInterval
export const useGetRepositoryThreatModel = threatModelHooks.useGet
export const invalidateGetRepositoryThreatModel = threatModelHooks.invalidateGet
export const usePostRepositoryThreatModel = threatModelHooks.usePost
export const useThreatModelStream = threatModelHooks.useStream

// The `/content` endpoint negotiates on `Accept`: `application/json` returns the structured
// `model_json` document (what we render), while the legacy default is `text/markdown`. We send the
// explicit JSON header so the backend never falls back to markdown. `generatedAt` participates in
// the query key so a regeneration invalidates the cached content.
export const useGetRepositoryThreatModelContent = ({
  repositoryId,
  generatedAt,
  enabled = true,
  ...rest
}: {
  repositoryId: string
  generatedAt?: string | null
  enabled?: boolean
} & Omit<UseQueryOptions<ThreatModelContent, ApiError>, 'queryKey' | 'queryFn'>) =>
  useQuery({
    queryKey: ['repository-threat-model-content', repositoryId, generatedAt ?? null],
    queryFn: () =>
      fetchWrapperQuery({
        path: buildPath('/api/v1/repositories/:repositoryId/threat-model/content', { repositoryId }),
        options: { headers: { 'X-Requested-With': 'JavaScript', Accept: 'application/json' } },
        schema: ThreatModelContentSchema,
        successStatusCodes: [200],
      }),
    enabled: enabled && !!repositoryId,
    ...rest,
  })

// #endregion

// #region Exploit Chains

const exploitChainsHooks = createGenerationResourceHooks<ExploitChainsResponse, ExploitChainsStreamEvent>({
  queryKeyBase: 'repository-exploit-chains',
  pathSegment: 'exploit-chains',
  responseSchema: ExploitChainsResponseSchema,
  streamEventSchema: ExploitChainsStreamEventSchema,
  phaseOptions: ExploitChainsPhaseSchema.options,
})

export const EXPLOIT_CHAINS_POLL_INTERVAL_MS = exploitChainsHooks.pollIntervalMs
export const getExploitChainsRefetchInterval = exploitChainsHooks.getRefetchInterval
export const useGetRepositoryExploitChains = exploitChainsHooks.useGet
export const invalidateGetRepositoryExploitChains = exploitChainsHooks.invalidateGet
export const usePostRepositoryExploitChains = exploitChainsHooks.usePost
export const useExploitChainsStream = exploitChainsHooks.useStream

// #endregion

// #region Finding Overlays

const findingOverlaysHooks = createGenerationResourceHooks<FindingOverlaysResponse, FindingOverlaysStreamEvent>({
  queryKeyBase: 'repository-finding-overlays',
  pathSegment: 'finding-overlays',
  responseSchema: FindingOverlaysResponseSchema,
  streamEventSchema: FindingOverlaysStreamEventSchema,
  phaseOptions: FindingOverlayPhaseSchema.options,
})

export const FINDING_OVERLAYS_POLL_INTERVAL_MS = findingOverlaysHooks.pollIntervalMs
export const getFindingOverlaysRefetchInterval = findingOverlaysHooks.getRefetchInterval
export const useGetRepositoryFindingOverlays = findingOverlaysHooks.useGet
export const invalidateGetRepositoryFindingOverlays = findingOverlaysHooks.invalidateGet
export const usePostRepositoryFindingOverlays = findingOverlaysHooks.usePost
export const useFindingOverlaysStream = findingOverlaysHooks.useStream

// finding-overlays /content returns structured JSON (narrative + priorities + clusters +
// assumptions) with its own schema, so — like threat-model above — it owns a bespoke content hook
// rather than the shared factory. `generatedAt` participates in the query key so a regeneration
// invalidates the cached content.
export const useGetRepositoryFindingOverlaysContent = ({
  repositoryId,
  generatedAt,
  enabled = true,
  ...rest
}: {
  repositoryId: string
  generatedAt?: string | null
  enabled?: boolean
} & Omit<UseQueryOptions<FindingOverlayResponse, ApiError>, 'queryKey' | 'queryFn'>) =>
  useQuery({
    queryKey: ['repository-finding-overlays-content', repositoryId, generatedAt ?? null],
    queryFn: () =>
      fetchWrapperQuery({
        path: buildPath('/api/v1/repositories/:repositoryId/finding-overlays/content', { repositoryId }),
        // Force JSON: the backend content-negotiates and otherwise falls back to markdown. Mirrors
        // `useGetRepositoryThreatModelContent`. The default headers include `X-Requested-With`, but
        // `options.headers` replaces the default object wholesale, so re-declare it here too.
        options: { headers: { 'X-Requested-With': 'JavaScript', Accept: 'application/json' } },
        schema: FindingOverlayResponseSchema,
        successStatusCodes: [200],
      }),
    enabled: enabled && !!repositoryId,
    ...rest,
  })

// #endregion

// #region Scorecards

/**
 * Scorecards mirror the threat-model / exploit-chains lifecycle (POST to (re)generate, poll GET
 * while `processing`, terminal on `completed`/`failed`) but the backend deliberately exposes no
 * SSE stream and no content sub-resource — the full report ships inline on the GET. So this uses
 * a bespoke poll-only pair of hooks rather than the shared `createGenerationResourceHooks`
 * factory, which is tuned to the stream + content sub-resource shape.
 */
export const SCORECARDS_POLL_INTERVAL_MS = 10_000
const SCORECARDS_BASE_PATH = '/api/v1/repositories/:repositoryId/scorecards' as const

export const getScorecardsRefetchInterval = (data: ScorecardsResponse | null | undefined): number | false =>
  data?.status === 'processing' ? SCORECARDS_POLL_INTERVAL_MS : false

const getRepositoryScorecardsOptions = ({
  repositoryId,
  enabled = true,
  ...rest
}: {
  repositoryId: string
  enabled?: boolean
} & Omit<UseQueryOptions<ScorecardsResponse | null, ApiError>, 'queryKey' | 'queryFn'>) =>
  queryOptions({
    queryKey: ['repository-scorecards', repositoryId],
    queryFn: () =>
      fetchWrapperQuery({
        path: buildPath(SCORECARDS_BASE_PATH, { repositoryId }),
        schema: z.union([ScorecardsResponseSchema, z.null()]),
        successStatusCodes: [200, 404],
        // A 404 means the resource has never been generated; surface it as `null` rather than
        // an error. While `processing`, the query polls until the status reaches terminal.
        deserializeFn: async (response: Response) => {
          if (response.status === 404) {
            return null
          }
          return (await response.json()) as unknown
        },
      }),
    refetchInterval: query => getScorecardsRefetchInterval(query.state.data),
    enabled: enabled && !!repositoryId,
    ...rest,
  })

export const useGetRepositoryScorecards = (input: Parameters<typeof getRepositoryScorecardsOptions>[0]) =>
  useQuery(getRepositoryScorecardsOptions(input))

export const invalidateGetRepositoryScorecards = (
  client: QueryClient,
  input: Parameters<typeof getRepositoryScorecardsOptions>[0]
) => client.invalidateQueries({ queryKey: getRepositoryScorecardsOptions(input).queryKey })

type PostRepositoryScorecardsVariables = { repositoryId: string }

export const usePostRepositoryScorecards = ({
  onSuccess,
  onError,
}: {
  onSuccess?: OnSuccess<ScorecardsResponse, PostRepositoryScorecardsVariables>
  onError?: OnError<ScorecardsResponse, PostRepositoryScorecardsVariables>
} = {}) =>
  useMutation({
    mutationFn: (variables: PostRepositoryScorecardsVariables) =>
      fetchWrapperMutation({
        path: buildPath(SCORECARDS_BASE_PATH, { repositoryId: variables.repositoryId }),
        options: { method: 'POST' },
        schema: ScorecardsResponseSchema,
        successStatusCodes: [202],
        deserializeFn: async response => (await response.json()) as unknown,
      }),
    onSuccess,
    onError,
  })

// #endregion

const getCodingGuidelinesOptions = ({
  organizationId = 'default-organization',
  enabled = true,
  ...rest
}: {
  organizationId?: string
  enabled?: boolean
} & Omit<UseQueryOptions<CodingGuidelinesResponse[], ApiError>, 'queryKey' | 'queryFn'> = {}) =>
  queryOptions({
    queryKey: ['coding-guidelines', organizationId],
    queryFn: () =>
      fetchWrapperQuery({
        path: buildPath('/api/v1/organizations/:organizationId/coding-guidelines', { organizationId }),
        schema: CodingGuidelinesResponseSchema.array(),
        successStatusCodes: [200],
      }),
    enabled,
    ...rest,
  })

export const useGetCodingGuidelines = (input?: Parameters<typeof getCodingGuidelinesOptions>[0]) =>
  useQuery(getCodingGuidelinesOptions(input))

const uploadWithProgress = ({
  path,
  formData,
  onProgress,
}: {
  path: string
  formData: FormData
  onProgress: (progressPercent: number) => unknown
}): Promise<CodingGuidelinesResponse> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.upload.addEventListener('progress', event => {
      if (event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100))
      }
    })
    xhr.addEventListener('load', () => {
      if (xhr.status === 201) {
        try {
          resolve(CodingGuidelinesResponseSchema.parse(JSON.parse(xhr.responseText) as unknown))
        } catch {
          reject(new ApiError(xhr.status, xhr.responseText, path))
        }
      } else {
        reject(new ApiError(xhr.status, xhr.responseText, path))
      }
    })
    xhr.addEventListener('error', () => reject(new ApiError(0, 'Network error', path)))
    xhr.open('POST', path)
    xhr.setRequestHeader('X-Requested-With', 'JavaScript')
    xhr.setRequestHeader('Accept', 'application/hal+json, application/json')
    xhr.send(formData)
  })

export const usePostCodingGuideline = ({
  organizationId = 'default-organization',
  onSuccess,
  ...options
}: {
  organizationId?: string
} & Omit<
  UseMutationOptions<
    CodingGuidelinesResponse,
    DefaultError,
    { displayName: string; file: File; onUploadProgress?: (progressPercent: number) => unknown }
  >,
  'mutationFn'
> = {}) => {
  const _onSuccess = useCallback<Exclude<typeof onSuccess, undefined>>(
    (data, variables, onMutateResult, context) => {
      context.client.invalidateQueries({ queryKey: getCodingGuidelinesOptions({ organizationId }).queryKey })
      onSuccess?.(data, variables, onMutateResult, context)
    },
    [organizationId, onSuccess]
  )

  return useMutation({
    mutationFn: async ({
      displayName,
      file,
      onUploadProgress,
    }: {
      displayName: string
      file: File
      onUploadProgress?: (progressPercent: number) => unknown
    }) => {
      const path = buildPath('/api/v1/organizations/:organizationId/coding-guidelines', { organizationId })
      const formData = new FormData()
      formData.append('display_name', displayName)
      formData.append('file', file)
      if (onUploadProgress) {
        return uploadWithProgress({ path, formData, onProgress: onUploadProgress })
      }
      return fetchWrapperMutation({
        path,
        options: { method: 'POST', body: formData, headers: { 'X-Requested-With': 'JavaScript' } },
        schema: CodingGuidelinesResponseSchema,
        successStatusCodes: [201],
        deserializeFn: response => response.json(),
      })
    },
    onSuccess: _onSuccess,
    ...options,
  })
}

export const usePatchCodingGuideline = ({
  organizationId = 'default-organization',
  onSuccess,
  ...options
}: {
  organizationId?: string
} & Omit<
  UseMutationOptions<CodingGuidelinesResponse, DefaultError, { id: string; body: UpdateCodingGuidelinesRequest }>,
  'mutationFn'
> = {}) => {
  const _onSuccess = useCallback<Exclude<typeof onSuccess, undefined>>(
    (data, variables, onMutateResult, context) => {
      context.client.invalidateQueries({ queryKey: getCodingGuidelinesOptions({ organizationId }).queryKey })
      onSuccess?.(data, variables, onMutateResult, context)
    },
    [organizationId, onSuccess]
  )

  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: UpdateCodingGuidelinesRequest }) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/organizations/:organizationId/coding-guidelines/:id', { organizationId, id }),
        options: {
          method: 'PATCH',
          body: JSON.stringify(body),
          headers: { 'Content-Type': 'application/json' },
        },
        schema: CodingGuidelinesResponseSchema,
        deserializeFn: response => response.json(),
      }),
    onSuccess: _onSuccess,
    ...options,
  })
}

export const useDeleteCodingGuideline = ({
  organizationId = 'default-organization',
  onSuccess,
  ...options
}: {
  organizationId?: string
} & Omit<UseMutationOptions<unknown, DefaultError, { id: string }>, 'mutationFn'> = {}) => {
  const _onSuccess = useCallback<Exclude<typeof onSuccess, undefined>>(
    (data, variables, onMutateResult, context) => {
      context.client.invalidateQueries({ queryKey: getCodingGuidelinesOptions({ organizationId }).queryKey })
      onSuccess?.(data, variables, onMutateResult, context)
    },
    [organizationId, onSuccess]
  )

  return useMutation({
    mutationFn: async ({ id }: { id: string }) =>
      fetchWrapperMutation({
        path: buildPath('/api/v1/organizations/:organizationId/coding-guidelines/:id', { organizationId, id }),
        options: { method: 'DELETE', headers: { 'X-Requested-With': 'JavaScript' } },
        schema: z.unknown(),
        successStatusCodes: [204],
      }),
    onSuccess: _onSuccess,
    ...options,
  })
}

/**
 * Triggers the in-cluster support-bundle service to generate a bundle and
 * upload it to the Pixee Vendor Portal in one shot.
 *
 * Pixee Enterprise Server only — the endpoint does not exist on SaaS, where
 * calls surface as a generic error toast in the UI.
 *
 * Status codes:
 * - 202: bundle generation started, body `{ id, phase: 'pending' }`
 * - 409: another bundle is already generating, body `{ error, id, phase }`
 */
const getPostSupportBundleOptions = (
  options: Omit<UseMutationOptions<SupportBundleResponse>, 'mutationFn' | 'mutationKey'> = {}
) =>
  mutationOptions({
    mutationKey: ['post-support-bundle'] as const,
    mutationFn: () =>
      fetchWrapperMutation({
        path: '/api/v1/support-bundles',
        options: { method: 'POST' },
        schema: SupportBundleResponseSchema,
        successStatusCodes: [202],
        deserializeFn: async response => (await response.json()) as unknown,
      }),
    ...options,
  })

export const usePostSupportBundle = (
  options: Omit<UseMutationOptions<SupportBundleResponse>, 'mutationFn' | 'mutationKey'> = {}
) => useMutation(getPostSupportBundleOptions(options))

/**
 * Fetches the API root. The presence of `_links.support-bundles` is the canonical signal that the
 * in-cluster support-bundle service is rendered on this install; user-platform features gate UI
 * affordances on that link.
 */
const getApiRootOptions = (options: Omit<UseQueryOptions<ApiRootResponse, ApiError>, 'queryKey' | 'queryFn'> = {}) =>
  queryOptions({
    queryKey: ['api-root'],
    queryFn: () =>
      fetchWrapperQuery({
        path: '/api/v1',
        schema: ApiRootResponseSchema,
      }),
    ...options,
  })

export const useApiRoot = (options: Omit<UseQueryOptions<ApiRootResponse, ApiError>, 'queryKey' | 'queryFn'> = {}) =>
  useQuery(getApiRootOptions(options))

// #endregion
